// /src/App.js
// Main application component - v10 Architecture Compliant
// Handles top-level routing, data loading, and theme management

import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import './App.css';

// Supabase client - import from shared location (protected wrapper)
import { supabase, supabaseRaw, supabaseFetch, enterDemoRead, exitDemoRead, isDemoReadActive, isDemoId, isKnownDemoId, demoModeHoldsFor, isUsableAccessToken, isDemoSessionExpired } from './lib/supabase';
import { isAppointedDemoAdmin, getDemoGroupBySlug } from './data/demoReadService';
import { authStorage } from './lib/authStorage';
import { configureAuthState } from './lib/protectedSupabase';
import { initServiceWorkerNavBridge } from './lib/pushNotifications';

// Analytics - privacy-preserving tracking
import { analytics } from './lib/analytics';

// Auth Required Modal (controlled directly, not via context for simpler integration)
import ConfirmationModal from './components/core/ConfirmationModal';
// Home Area Self-Healer (replaces LocationSetupModal)
import { hasHomeArea, getAccountClosureStatus } from './data/accountService';
import { parseCallbackCarrier, parseCallbackError, isInvitationTokenShape } from './utils/authFlow';
import { resolveScopeBoundaryIds, baseParishId } from './utils/scopeResolution';
import { DEFAULT_COUNTRY_CODE, getAreaCountryCode, getBoundaryPlace } from './data/countriesService';

// Theme System
import { ThemeProvider } from './themes/ThemeProvider';

// Auth Context
import { AuthProvider } from './contexts/AuthContext';

// Translation System
import { TranslationProvider } from './i18n/useTranslation';

// Main Feed Component
import MainFeed from './components/MainFeed';

// Detail View Components
import ArticleDetail from './components/news/ArticleDetail';
import ChatDetail from './components/chat/ChatDetail';
import EventsDetail from './components/events/EventsDetail';

// User Management Components
import MyProfile from './components/user/MyProfile';
import MyArticles from './components/user/MyArticles';
import MyTags from './components/user/MyTags';
import MyLocations from './components/user/MyLocations';
import MyImages from './components/user/MyImages';
import StewardWorkspace from './components/user/StewardWorkspace';
import EventsImporter from './components/events/EventsImporter';
import PendingEvents from './components/events/PendingEvents';
import AddEditArticle from './components/news/AddEditArticle';
import WalkingComposer from './components/news/WalkingComposer';

// Chat Management Components
import AddEditChat from './components/chat/AddEditChat';

// Event Management Components
import AddEditEvent from './components/events/AddEditEvent';

// Group Management Components
import AddEditGroup from './components/groups/AddEditGroup';
import AdminGroup from './components/groups/AdminGroup';
import AcceptInvitation from './components/groups/AcceptInvitation';
import JoinGroupLink from './components/groups/JoinGroupLink';
import InvitationEmailAction from './components/groups/InvitationEmailAction';
import AddEditLandingPageGroup from './components/groups/AddEditLandingPageGroup';
import LandingPageGroup from './components/groups/LandingPageGroup';

// Admin Components
import AdminAnalytics from './components/admin/AdminAnalytics';
import AdminPanel from './components/admin/AdminPanel';

// Auth Components
import LoginRegisterModal from './components/auth/LoginRegisterModal';
import ResetPasswordModal from './components/auth/ResetPasswordModal';

// Smart Link Components
import SmartLinkPreview from './components/core/SmartLinkPreview';
import SignInNotice from './components/core/SignInNotice';
import AccountReturnModal from './components/user/AccountReturnModal';
import AccountSetup from './components/auth/AccountSetup';
import NoticeStrip from './components/core/NoticeStrip';


// Demo Components
import DemoWatermark from './components/demo/DemoWatermark';

// Authority Components
import AuthorityVerifyPage from './components/authority/AuthorityVerifyPage';

// Public directory-suggestion landing page (no account)
import SuggestPage from './components/frontpage/SuggestPage';

// Data Services
import {
  communityService,
  chatService,
  articleService,
  eventService,
  groupService,
  smartLinkService
} from './data/dataService';

// IP Geolocation and Geocentric Queries
import { getLocationFromIP } from './utils/ipGeolocation';
import { reverseGeocodeToLocality } from './utils/geocentricQueries';

// Responsive breakpoint (1200px) — drives the viewport-flip reconciliation effect below.
import { useMediaQuery } from './hooks/useMediaQuery';

// Global error capture and navigation breadcrumb trail for problem reports
import { initErrorCapture } from './utils/errorCapture';
import { IDENTITY_UNKNOWN, identityResetNeeded, applyCleanStart, LINK_VIEWS } from './utils/identityReset';
initErrorCapture();

// The one boundary that names an area and places its weather: the member's own parish
// (baseParishId — the base parish, else the first saved boundary). get_boundary_centroid picks an
// arbitrary one of several same-level parishes, so it is never given the whole area
// (docs/briefs/2026-09-28-weather-uses-base-parish.md; 2026-09-29-own-parish-everywhere.md).
// The invitation pages (#54). The new-account setup stands aside on them: joining comes first (R5).
const INVITATION_VIEWS = new Set(['accept-invitation', 'join-group-link', 'invitation-optout', 'invitation-report']);
const INVITATION_HASH_TYPES = ['invite', 'join', 'invitation-optout', 'invitation-report'];

const headlineBoundaryIds = (area) => {
  const id = baseParishId(area);
  return id != null ? [id] : [];
};

function App() {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  
  // View State
  const [currentView, setCurrentView] = useState('feed'); // 'feed', 'article', 'chat', 'event', 'profile', 'articles', 'tags', 'locations', 'new-article', 'new-chat', 'new-event', 'new-group', 'admin-group', 'admin-analytics', 'admin', 'group-landing'
  const [currentContext, setCurrentContext] = useState('locality'); // 'locality' or 'groups'
  const [previousContext, setPreviousContext] = useState(null); // Save context when entering My pages
  const [previousView, setPreviousView] = useState(null); // Save view when entering new-chat or other forms
  const [previousGroup, setPreviousGroup] = useState(null); // Tier 2: group navigated FROM, for the §4 gate Back
  const [landingFromGate, setLandingFromGate] = useState(false); // Tier 2: landing-view reached via the §4 gate redirect (not "…more")
  
  // Loading State
  const [loading, setLoading] = useState(true);
  
  // Data State
  const [articles, setArticles] = useState([]);
  // Companion has-more flag for `articles` — threaded explicitly rather than riding as a
  // property on the array (which silently drops across any .filter()/.map() between here and
  // a leaf component; docs/briefs/2026-08-13-more-control-phase-a.md §1 Q1). setArticlesWithMeta
  // is the only place that writes either piece of state, so the two can never drift apart.
  const [articlesHasMore, setArticlesHasMore] = useState(false);

  // Load More for the locality News and Events lists (Roadmap #104 code item 1;
  // docs/briefs/2026-10-18-feed-dam-ranking-phase2-events-addendum-5.md). Each press re-asks for
  // 50 more: the feed's rank order is deterministic, so a larger limit returns the rows already
  // shown plus the next by rank. What the next press needs — the last locality request, the
  // limits, a generation per list and how many rows are shown — lives in a ref, because some loads
  // run from listeners registered once and would read stale state (Advisor ADVISE 4). Every write
  // of articles or events goes through setArticlesWithMeta / setEventsWithMeta, which bump that
  // list's generation, so a Load More answer that arrives after the list was replaced (a new scope,
  // a group, a sign-out) is dropped. The identity reset replaces the whole ref (Advisor SA-1).
  const FEED_PAGE = 50;
  const freshFeedPaging = () => ({
    newsRequest: null, newsKey: null, newsLimit: FEED_PAGE, newsGen: 0, newsCount: 0,
    eventsRequest: null, eventsKey: null, futureLimit: FEED_PAGE, pastLimit: FEED_PAGE, eventsGen: 0, eventsCount: 0
  });
  const feedPagingRef = useRef(freshFeedPaging());
  const loadingMoreRef = useRef(false);
  const [eventsHasMore, setEventsHasMore] = useState({ future: false, past: false });
  const [loadingMore, setLoadingMore] = useState(null); // 'news' | 'future' | 'past' | null

  const setArticlesWithMeta = (data) => {
    const list = data || [];
    feedPagingRef.current.newsGen += 1;
    feedPagingRef.current.newsCount = list.length;
    setArticles(list);
    setArticlesHasMore(Boolean(list.hasMore));
  };
  const [chats, setChats] = useState([]);
  const [events, setEvents] = useState([]);
  // The one writer of events, as setArticlesWithMeta is for articles: the list, and whether each
  // window (Future, Past) has more. A list without the window flags (the no-location fallback, a
  // group's events) uses its single hasMore for both.
  const setEventsWithMeta = (data) => {
    const list = data || [];
    feedPagingRef.current.eventsGen += 1;
    feedPagingRef.current.eventsCount = list.length;
    setEvents(list);
    setEventsHasMore({
      future: Boolean(list.hasMoreFuture ?? list.hasMore),
      past: Boolean(list.hasMorePast ?? list.hasMore)
    });
  };

  // Every locality News / Events load goes through these, so the next Load More knows what to
  // re-ask. A different scope (area, boundaries, guest location) starts again at 50; a refresh of
  // the same scope keeps the limit, so Back from an item keeps the rows that were loaded (ADVISE 3).
  const feedScopeKey = (areaId, options = {}) =>
    JSON.stringify([areaId ?? null, options.boundaryIds ?? null, options.guestCoords ?? null]);
  const fetchLocalityNews = (areaId, options = {}) => {
    const fp = feedPagingRef.current;
    const key = feedScopeKey(areaId, options);
    if (fp.newsKey !== key) { fp.newsKey = key; fp.newsLimit = FEED_PAGE; }
    fp.newsRequest = { areaId, options };
    fp.newsGen += 1;
    return articleService.getLocalityNews(areaId, { ...options, limit: fp.newsLimit });
  };
  const fetchLocalityEvents = (areaId, options = {}) => {
    const fp = feedPagingRef.current;
    const key = feedScopeKey(areaId, options);
    if (fp.eventsKey !== key) { fp.eventsKey = key; fp.futureLimit = FEED_PAGE; fp.pastLimit = FEED_PAGE; }
    fp.eventsRequest = { areaId, options };
    fp.eventsGen += 1;
    return eventService.getLocalityEvents(areaId, { ...options, futureLimit: fp.futureLimit, pastLimit: fp.pastLimit });
  };

  // One press, one request. The answer is dropped if the list was replaced meanwhile, or if it is
  // shorter than what is on screen (a failed call returns an empty list; it must not wipe one).
  const loadMoreNews = async () => {
    const fp = feedPagingRef.current;
    if (!fp.newsRequest || loadingMoreRef.current) return;
    const gen = fp.newsGen;
    const limit = fp.newsLimit + FEED_PAGE;
    loadingMoreRef.current = true;
    setLoadingMore('news');
    try {
      const { areaId, options } = fp.newsRequest;
      const data = await articleService.getLocalityNews(areaId, { ...options, limit });
      if (feedPagingRef.current !== fp || fp.newsGen !== gen || (data || []).length < fp.newsCount) return;
      fp.newsLimit = limit;
      setArticlesWithMeta(data);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(null);
    }
  };
  const loadMoreEvents = async (window) => {
    const fp = feedPagingRef.current;
    if (!fp.eventsRequest || loadingMoreRef.current || (window !== 'future' && window !== 'past')) return;
    const gen = fp.eventsGen;
    const futureLimit = window === 'future' ? fp.futureLimit + FEED_PAGE : fp.futureLimit;
    const pastLimit = window === 'past' ? fp.pastLimit + FEED_PAGE : fp.pastLimit;
    loadingMoreRef.current = true;
    setLoadingMore(window);
    try {
      const { areaId, options } = fp.eventsRequest;
      const data = await eventService.getLocalityEvents(areaId, { ...options, futureLimit, pastLimit });
      if (feedPagingRef.current !== fp || fp.eventsGen !== gen || (data || []).length < fp.eventsCount) return;
      fp.futureLimit = futureLimit;
      fp.pastLimit = pastLimit;
      setEventsWithMeta(data);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(null);
    }
  };
  const [currentCommunity, setCurrentCommunity] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Search State
  const [articleSearchTerm, setArticleSearchTerm] = useState('');
  const [searchedArticles, setSearchedArticles] = useState([]);
  const [userHomeLocationName, setUserHomeLocationName] = useState('My Location'); // User's home area display name
  const [userHomeLocationCoords, setUserHomeLocationCoords] = useState(null); // { lat, lon } for weather widget
  const [detectedBoundaryId, setDetectedBoundaryId] = useState(null); // Resolved admin_boundaries id for guest browse location
  // Temporary visit (Change 3, logged-in only): session-only scope override, never persisted and
  // never written to user_areas. { boundaryIds: [id], displayName } | null. Feed views and chat
  // creation honour it; article/event creation deliberately never reads it (addendum-1 §C).
  const [visitArea, setVisitArea] = useState(null);
  
  // Selected Items for Detail Views
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [selectedChatResponses, setSelectedChatResponses] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // Article Editor State
  const [articleMode, setArticleMode] = useState('add'); // 'add' or 'edit'
  const [articleToEdit, setArticleToEdit] = useState(null);
  const [preselectedGroupId, setPreselectedGroupId] = useState(null); // Group to preselect when creating article

  // Chat Editor State
  const [chatMode, setChatMode] = useState('add'); // 'add' or 'edit'
  const [chatToEdit, setChatToEdit] = useState(null);
  const [preselectedChatGroupId, setPreselectedChatGroupId] = useState(null); // Group to preselect when creating chat

  // Front-page chat accordion state — permalink auto-expand
  const [frontPageOpenChatId, setFrontPageOpenChatId] = useState(null);

  // Event Editor State
  const [eventMode, setEventMode] = useState('add'); // 'add' or 'edit'
  const [eventToEdit, setEventToEdit] = useState(null);
  const [preselectedEventGroupId, setPreselectedEventGroupId] = useState(null); // Group to preselect when creating event

  // Group Editor State
  const [groupMode, setGroupMode] = useState('add'); // 'add' or 'edit'
  const [groupToEdit, setGroupToEdit] = useState(null);
  const [postCreateGroup, setPostCreateGroup] = useState(false); // True when navigating to admin after group creation
  const [parentGroupForNew, setParentGroupForNew] = useState(null); // Pre-selected parent for creating subgroup
  const [groupPrefill, setGroupPrefill] = useState(null); // Directory→group prefill (name/description/boundary/tags + link intent)
  const [addEditLandingPageGroup, setAddEditLandingPageGroup] = useState(null); // Group for landing page editor
  const [postCreateLandingPage, setPostCreateLandingPage] = useState(false); // True when the landing editor is the setup-end handoff
  const [landingPageGroup, setLandingPageGroup] = useState(null); // Group for landing page read-only view

  // Profile Viewing State
  const [selectedProfileUserId, setSelectedProfileUserId] = useState(null);

  // Feed State - Track selected group and mode across view changes
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedArea, setSelectedArea] = useState(null); // Preserve selected locality area across view changes
  const [currentMode, setCurrentMode] = useState('news'); // 'news', 'chat', 'events'
  // The country in view (Roadmap #85, R1 + addendum 1 A3): one value, derived once, below; the
  // header's flag and the language rule both read it through TranslationProvider.
  const [countryInView, setCountryInView] = useState(DEFAULT_COUNTRY_CODE);
  const [frontPageActive, setFrontPageActive] = useState(true); // Locality desktop front page vs mode grid — survives MainFeed unmount/remount
  const [mobileScreen, setMobileScreen] = useState('news'); // Mobile front page active screen: 'directory' | 'news' | 'chat' | 'events' | 'marketplace'
  const [groupsInitialMode, setGroupsInitialMode] = useState('joined'); // 'joined' or 'owned' for GroupsGrid
  const [feedScrollPosition, setFeedScrollPosition] = useState(0); // Preserve scroll position when navigating away from feed
  const [shouldRestoreScroll, setShouldRestoreScroll] = useState(false); // Flag to trigger scroll restoration on feed mount

  // ── Viewport-flip reconciliation (1200px boundary) ───────────────────────────
  // currentMode / frontPageActive / mobileScreen live here and are otherwise never keyed to
  // width, so crossing the 1200px desktop↔mobile boundary (DevTools/responsive resize) would
  // strand the user in a stale view — e.g. a mobile drawer mode-switch sets frontPageActive=false,
  // then resizing up lands on a desktop mode grid instead of the front page. On an ACTUAL crossing
  // we re-assert the front page and reconcile the two mode variables so whichever interface mounts
  // lands on a coherent surface. State reset only — no render-branch changes.
  //
  // Side effect we rely on elsewhere: forcing frontPageActive=true whenever we land on mobile
  // neutralises the latent mobile-locality frontPageActive===false path (nothing on mobile sets it
  // false on its own — there is no mobile NewsBar), which is what makes the old NavigationDrawer
  // fully dead for deletion in the Commit 4 cleanup.
  //
  // (Real devices never cross 1200px mid-session, so this is correctness for emulator/resize, not
  // a live-user bug.)
  const { isMobile } = useMediaQuery();
  const prevIsMobileRef = useRef(isMobile);
  useEffect(() => {
    if (prevIsMobileRef.current === isMobile) return; // only on a real crossing (also skips mount)
    prevIsMobileRef.current = isMobile;

    setFrontPageActive(true);
    if (isMobile) {
      // → mobile: the five-tab bar reads mobileScreen; seed it from the desktop mode (1:1 for
      //   news/chat/events).
      setMobileScreen(currentMode);
    } else {
      // → desktop: the mode grid reads currentMode; seed it from mobileScreen, collapsing the
      //   mobile-only Directory/Marketplace screens (no desktop-mode equivalent) to 'news'.
      setCurrentMode(['news', 'chat', 'events'].includes(mobileScreen) ? mobileScreen : 'news');
    }
  }, [isMobile, currentMode, mobileScreen]);

  // Auth State
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginModalMode, setLoginModalMode] = useState('login'); // which view LoginRegisterModal opens on ('login' | 'register')

  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null); // Store navigation action when user needs to login

  // Auth-gate reassurance copy carried into the login modal when a gated action
  // (Follow, comment, create event, …) opens it. Empty → generic contribution prompt.
  const [authRequiredMessage, setAuthRequiredMessage] = useState('');
  const [appModal, setAppModal] = useState({ visible: false, message: '' });
  const showAppModal = (message) => setAppModal({ visible: true, message });

  // Hash routing state - track if we need to navigate after data loads
  const [pendingHashNavigation, setPendingHashNavigation] = useState(null);

  // Invitation token state (for /invite/{token} URLs)
  const [inviteToken, setInviteToken] = useState(null);
  // Shared join link (#/join/{token}) and the invitation email's opt-out / report pages
  // (#/invitation-optout/{token}, #/invitation-report/{token}) — Invitations Build 2 Phase 3 (#54).
  const [joinToken, setJoinToken] = useState(null);
  const [emailActionToken, setEmailActionToken] = useState(null);


  // Authority verify state (for /verify-authority/{token} URLs)
  const [authorityVerifyToken, setAuthorityVerifyToken] = useState(null);

  // Smart Link state (for /l/{slug} URLs)
  const [showSmartLinkPreview, setShowSmartLinkPreview] = useState(false);
  const [smartLinkData, setSmartLinkData] = useState(null);
  const [smartLinkTarget, setSmartLinkTarget] = useState(null);
  const [smartLinkLoading, setSmartLinkLoading] = useState(false);

  // Guest articles protection ref - prevents clearing guest articles on context switch
  const guestArticlesLoadedRef = useRef(false);

  // Active view context — the live source of truth for which feed is showing
  // ('locality' or 'group:<id>'). Every async content loader captures its target
  // context and re-checks this ref immediately before setState; stale results are
  // discarded. Kills the group-view leak (locality feed appearing inside a group).
  // Guard principle: ANY new loader that writes to the shared articles/chats/events
  // state MUST capture its stamp and check activeContextRef.current before setState.
  const activeContextRef = useRef('locality');

  // Demo mode (Phase B1 — docs/escalations/2026-09-18-phase-b1-schema-targeting-design.md, accepted
  // 18 Sep 2026). While an appointed demo admin views /demo/:slug, supabaseFetch sends reads of the
  // moved tables to the `demo` schema (see src/lib/supabase.js). The target's lifetime is bound to
  // provenance: it stays on only while what is on screen came from `demo`, and is cleared
  // synchronously at the points this component stamps activeContextRef (stampContext below), when
  // an item that did not come from `demo` is opened, on any view outside the demo experience, and
  // on sign-out or a change of user. isDemoMode mirrors it for rendering (watermark, hidden search).
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [demoRouteCheck, setDemoRouteCheck] = useState(0); // bumped to re-run the /demo route after sign-in
  // The demo session died (docs/briefs/2026-09-25-demo-read-expired-session.md + addendum 1).
  // supabaseFetch refuses a demo-targeted request without a usable token rather than falling back to
  // the anon key, which could only ever 401 and be swallowed into an empty page. The callers in
  // between swallow errors by long-standing convention, so the signal arrives as an event. One
  // notice per page: setting true when already true is a no-op, so a burst of refused requests
  // (a group page fires several at once) still shows exactly one strip.
  const [demoSessionExpired, setDemoSessionExpired] = useState(false);
  const demoUserIdRef = useRef(null); // who entered demo mode — a different signed-in user leaves it
  const demoGateLoginRef = useRef(false); // the demo gate opened the login modal → re-run the route on sign-in

  // Leave demo mode. replaceUrl: swap a /demo/… URL for / so a reload does not re-enter.
  const leaveDemoMode = ({ replaceUrl = true } = {}) => {
    setIsDemoMode(false); // always — the render flag must never outlive the target
    if (!isDemoReadActive()) return;
    exitDemoRead();
    demoUserIdRef.current = null;
    if (replaceUrl && /^\/demo\//.test(window.location.pathname)) {
      window.history.replaceState(null, '', '/' + window.location.hash);
    }
  };

  // Leave demo mode unless this id was read from `demo` in the current demo session.
  const leaveDemoUnlessDemoId = (id) => {
    if (isDemoReadActive() && !isDemoId(id)) leaveDemoMode();
  };

  // Leaving the demo experience by view: anything other than an item detail (article/event/chat —
  // guarded by provenance in their handlers) or a demo group's own feed/landing page ends demo
  // mode. A layout effect, so it runs before any child's data-fetching (passive) effect for the
  // new view — a profile or admin page never reads with the demo target still on.
  useLayoutEffect(() => {
    if (!isDemoReadActive()) return;
    const holds = demoModeHoldsFor({
      view: currentView,
      context: currentContext,
      groupId: selectedGroup?.id,
      landingGroupId: landingPageGroup?.id,
      chatGroupId: preselectedChatGroupId || chatToEdit?.community_id,
      chatId: chatToEdit?.id
    });
    if (!holds) leaveDemoMode();
  }, [currentView, currentContext, selectedGroup?.id, landingPageGroup?.id, preselectedChatGroupId, chatToEdit?.id, chatToEdit?.community_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Every write to activeContextRef goes through here, so the demo target can never outlive the
  // content context it belongs to: a stamp for anything but a group read from `demo` ends it.
  const stampContext = (context) => {
    activeContextRef.current = context;
    if (isDemoReadActive() && !(context.startsWith('group:') && isDemoId(context.slice('group:'.length)))) {
      leaveDemoMode();
    }
  };

  // supabaseFetch refused a demo-targeted request because the session is dead. Raise the notice.
  // Deliberately does NOT call leaveDemoMode(): ruling 3 of the brief — being thrown back to the
  // live site mid-demonstration is the worse outcome. The watermark and the /demo/… URL stay put.
  useEffect(() => {
    const onDemoSessionExpired = () => setDemoSessionExpired(true);
    window.addEventListener('ournet:demo-session-expired', onDemoSessionExpired);
    return () => window.removeEventListener('ournet:demo-session-expired', onDemoSessionExpired);
  }, []);

  // The notice's action. Signing in is the fix, not reloading: a reload re-runs the demo route
  // against the same dead session and lands right back here. Arming demoGateLoginRef is the
  // existing mechanism (see the auth listener) that re-runs /demo/:slug once a session exists, so
  // the demo comes back by itself without the user retyping the URL.
  const handleDemoSessionSignIn = () => {
    demoGateLoginRef.current = true;
    setLoginModalMode('login');
    setShowLoginModal(true);
  };

  // Smart link article locality context — used to load the article's locality feed when guest clicks back
  // Stores { localityIds, lat, lng, locationName } from the shared article
  const smartLinkLocalityRef = useRef(null);

  // Mirror of currentView, read by handlers that can be invoked from a stale
  // closure — e.g. the ournet:navigate listener below is registered on
  // [currentUser, currentContext], so if it dispatches while neither has
  // changed, its closure over handleShowEventsImporter/handleShowEventsPending/
  // handleShowStewardWorkspace (and the currentView those captured) can be
  // stale. Reading currentViewRef.current instead of currentView in those three
  // handlers' setPreviousView(...) calls fixes it without re-registering the
  // listener on every view change (Phase 5a §3.8 — same ref-mirror idiom as
  // activeContextRef/detectedBoundaryIdRef below).
  const currentViewRef = useRef(currentView);
  useEffect(() => { currentViewRef.current = currentView; }, [currentView]);

  // The country in view (Roadmap #85, R1; addendum 1 A3 — same source the header used): for a
  // member, the country of their selected area's base parish; for a visitor, the country of the
  // place they picked or landed on (detectedBoundaryId); PT when there is none. A temporary visit
  // does not change it (R1: "the country of their selected area"). Keyed on the member's id, not on
  // currentUser being truthy — a visitor's currentUser is a guest object with id: null.
  useEffect(() => {
    let cancelled = false;
    const resolveCountry = currentUser?.id
      ? getAreaCountryCode(selectedArea)
      : getBoundaryPlace(detectedBoundaryId).then(place => place?.countryCode || null);
    resolveCountry
      .then(code => { if (!cancelled) setCountryInView(code || DEFAULT_COUNTRY_CODE); })
      .catch(() => { if (!cancelled) setCountryInView(DEFAULT_COUNTRY_CODE); });
    return () => { cancelled = true; };
  }, [currentUser?.id, selectedArea?.id, selectedArea?.primary_boundary_id, detectedBoundaryId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Self-healer: mirrors the identity the self-healer last ran for — NOT "has it ever run".
  // It is compared for INEQUALITY, never for presence: `!ref.current` answers "has this ever
  // run", which is a different question from "is this the same user", and the two diverge
  // exactly when a second account appears in a live session. That divergence was the 15 Sep
  // post-confirmation defect — see the guard at the self-healer effect below.
  const previousUserIdRef = useRef(null);
  const detectedBoundaryIdRef = useRef(null); // Mirror of detectedBoundaryId for self-healer closure
  // Mirror of visitArea for the stale-closure-prone event listeners below (area-changed,
  // articlesChanged, locations-refresh, article-search) — same idiom as detectedBoundaryIdRef.
  const visitAreaRef = useRef(null);
  const [autoHomeHighlight, setAutoHomeHighlight] = useState(false); // Passes highlight hint to MyLocations
  // One-shot sign-in strip (2026-09-16, stale-session-guard brief items 3 and 4). Holds exactly
  // one of SignInNotice's three statuses, or null. Same one-shot idiom as autoHomeHighlight
  // above: App owns the flag, a component INSIDE <TranslationProvider> renders the strings, so
  // no new hardcoded English enters App.js's pre-provider body (TRANSLATION.md § Scoped
  // exception — that carve-out is exact and must not be widened). Not a notification queue.
  const [signInNotice, setSignInNotice] = useState(null);
  // New-account setup gate (Roadmap #88, registration confirm-first R4): for the member it was read
  // for, whether they still need to choose their place. needed: null = not known yet (or the read
  // failed — then nothing is shown), true = show AccountSetup, false = they have a home.
  const [setupState, setSetupState] = useState({ userId: null, needed: null });
  // Re-define-home hand-off (2026-09-01 home-picker-redefine-flow-fix-build): the core boundary
  // MyProfile's existing-home picker just resolved, carried into MyLocations so it can seed a new
  // area from it in a "set-home session". Same shape as new-group's own `prefill` (App.js:2650) —
  // a same-session, synchronous navigate-event payload, not a stash (registration's stash exists to
  // survive the pre-SIGNED_IN gap; an existing-home user is already authenticated, so that gap
  // doesn't apply here). Cleared on MyLocations' own onBack, matching autoHomeHighlight's own
  // clear-on-back convention right below it.
  const [myLocationsSetHomeSeed, setMyLocationsSetHomeSeed] = useState(null); // { boundaryId } | null

  // ============================================================================
  // CONFIGURE PROTECTED SUPABASE
  // ============================================================================

  // Configure the protected Supabase wrapper with current auth state
  // This enables the site-wide "login required" popup for database mutations
  useEffect(() => {
    configureAuthState({
      isLoggedIn,
      currentUserId: currentUser?.id,
      showAuthModal: (message) => {
        console.log('Protected mutation blocked - showing login modal');
        setAuthRequiredMessage(message || 'gate_body_generic');
        setLoginModalMode('login');
        setShowLoginModal(true);
      }
    });
  }, [isLoggedIn, currentUser]);

  useEffect(() => {
    // The retired registration wizard's browser stash (#88): browsers that used it still hold it,
    // possibly with About-me text. Nothing reads it any more; clear it once.
    try { localStorage.removeItem('ournet_wizard_registration'); } catch { /* storage unavailable */ }
  }, []);

  // Request persistent storage so the browser (Android Chrome especially) doesn't
  // evict the auth session under memory pressure — IndexedDB + localStorage are
  // "best-effort" until granted. Runs once on mount; safe no-op where unsupported.
  useEffect(() => {
    if (navigator.storage?.persist) {
      navigator.storage.persisted()
        .then((isPersisted) => {
          if (isPersisted) {
            console.log('🔒 Persistent storage already granted');
            return;
          }
          return navigator.storage.persist().then((granted) => {
            console.log('🔒 Persistent storage', granted ? 'granted' : 'denied');
          });
        })
        .catch(() => { /* storage API unavailable — ignore */ });
    }
  }, []);

  // ============================================================================
  // HOME AREA SELF-HEALER
  // ============================================================================

  // On any authenticated-identity CHANGE, ask whether the member has a home area. If not, they
  // set up their account (AccountSetup, rendered below) — Roadmap #88, registration confirm-first
  // (docs/briefs/2026-10-03-registration-confirm-first.md R4 + addendum 1 item 2). Nothing is
  // created automatically any more: the old automatic home from the visitor's browsed area or from
  // geolocation, the wizard's browser stash and the required map picker are retired — the member
  // chooses their own place.
  //
  // The identity test is the 2026-09-16 one (docs/briefs/2026-09-15-fix-stale-session-guard-
  // post-confirmation.md item 1): it fires on null→value AND value→other-value, so confirming in a
  // tab that already held another account's session is handled like a fresh sign-in.
  // (Advisor A1) The answer is reset to "not known" at once on every identity change, a late answer
  // for an earlier identity is dropped, and a failed read shows nothing — a member who has a home
  // must never be put behind setup by a network blip. (A7) A member closing their account is not
  // asked to set one up (the database would refuse it; AccountReturnModal has that moment).
  useEffect(() => {
    const userId = currentUser?.id || null;
    if (userId !== previousUserIdRef.current) {
      setSetupState({ userId, needed: null });
      if (userId) {
        Promise.all([hasHomeArea(userId), getAccountClosureStatus()]).then(([home, closure]) => {
          if (home.error || home.data === null) {
            console.warn('Setup gate: home area could not be read; not asking.', home.error);
            return;
          }
          const closing = closure?.data?.closing === true;
          setSetupState(prev => (prev.userId === userId
            ? { userId, needed: !home.data && !closing }
            : prev));
        }).catch((err) => console.warn('Setup gate: could not be read; not asking.', err));
      }
    }
    previousUserIdRef.current = userId;
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Setup finished: the gate closes. One landing rule (Advisor A6): a member who was looking at a
  // group (an invited newcomer who joined first, R5) stays on it; anyone else lands on the
  // locality feed with their new area.
  const handleSetupComplete = () => {
    setSetupState(prev => ({ ...prev, needed: false }));
    if (!(currentContext === 'groups' && selectedGroup)) {
      setCurrentView('feed');
    }
  };

  // ============================================================================
  // CLEAN START ON AN IDENTITY CHANGE (Roadmap #100)
  // ============================================================================

  // Signing out, or signing in as a different account, returns the app to a clean start: nothing
  // loaded for the previous account survives — not its group, its posts, chats and events, its
  // open item, its area. docs/briefs/2026-10-16-sign-out-clears-the-group.md Part 2, addendum 1.
  // One reset, called from all four auth paths: handleLogout, the SIGNED_OUT listener (another
  // tab, expiry), handleLoginSuccess and the SIGNED_IN listener. Which changes reset is decided in
  // utils/identityReset.js (identityResetNeeded); note it is a THIRD identity test in this file,
  // distinct on purpose from previousUserIdRef (setup gate) and demoUserIdRef (demo mode) — an
  // observed guest signing in keeps their page, so an invited newcomer stays on the group (R5).
  //
  // The last signed-in id is recorded wherever currentUser becomes known, including the cold-load
  // paths — never from a separate storage read, which can be empty on Android (Advisor SA-1).
  // Until it is known it is IDENTITY_UNKNOWN, which resets: an unknown identity fails closed.
  const lastSignedInIdRef = useRef(IDENTITY_UNKNOWN);
  useEffect(() => {
    if (currentUser) lastSignedInIdRef.current = currentUser.id || null; // the guest object has id null
  }, [currentUser]);

  // Called from listeners registered once, so it reads only setters and refs, never state.
  const resetForIdentityChange = (kind, nextUserId = null) => {
    const lastId = lastSignedInIdRef.current;
    lastSignedInIdRef.current = kind === 'signed-out' ? null : nextUserId;
    if (!identityResetNeeded(kind, lastId, nextUserId)) return;
    console.log('🧼 Identity changed - clean start:', kind);

    // A view opened by a link (an invitation being accepted) is keyed to the link, not the account.
    const linkView = LINK_VIEWS.has(currentViewRef.current) ? { currentView: currentViewRef.current } : {};
    const missing = applyCleanStart({
      articles: setArticles,
      articlesHasMore: setArticlesHasMore,
      chats: setChats,
      events: setEvents,
      eventsHasMore: setEventsHasMore,
      loadingMore: setLoadingMore,
      searchedArticles: setSearchedArticles,
      articleSearchTerm: setArticleSearchTerm,
      selectedArticle: setSelectedArticle,
      selectedChat: setSelectedChat,
      selectedChatResponses: setSelectedChatResponses,
      selectedEvent: setSelectedEvent,
      currentView: setCurrentView,
      currentContext: setCurrentContext,
      previousContext: setPreviousContext,
      previousView: setPreviousView,
      previousGroup: setPreviousGroup,
      landingFromGate: setLandingFromGate,
      currentCommunity: setCurrentCommunity,
      selectedGroup: setSelectedGroup,
      selectedArea: setSelectedArea,
      visitArea: setVisitArea,
      currentMode: setCurrentMode,
      frontPageActive: setFrontPageActive,
      mobileScreen: setMobileScreen,
      groupsInitialMode: setGroupsInitialMode,
      feedScrollPosition: setFeedScrollPosition,
      shouldRestoreScroll: setShouldRestoreScroll,
      frontPageOpenChatId: setFrontPageOpenChatId,
      selectedProfileUserId: setSelectedProfileUserId,
      articleMode: setArticleMode,
      articleToEdit: setArticleToEdit,
      preselectedGroupId: setPreselectedGroupId,
      chatMode: setChatMode,
      chatToEdit: setChatToEdit,
      preselectedChatGroupId: setPreselectedChatGroupId,
      eventMode: setEventMode,
      eventToEdit: setEventToEdit,
      preselectedEventGroupId: setPreselectedEventGroupId,
      groupMode: setGroupMode,
      groupToEdit: setGroupToEdit,
      postCreateGroup: setPostCreateGroup,
      parentGroupForNew: setParentGroupForNew,
      groupPrefill: setGroupPrefill,
      addEditLandingPageGroup: setAddEditLandingPageGroup,
      postCreateLandingPage: setPostCreateLandingPage,
      landingPageGroup: setLandingPageGroup,
      showSmartLinkPreview: setShowSmartLinkPreview,
      smartLinkData: setSmartLinkData,
      smartLinkTarget: setSmartLinkTarget,
      smartLinkLoading: setSmartLinkLoading,
      autoHomeHighlight: setAutoHomeHighlight,
      myLocationsSetHomeSeed: setMyLocationsSetHomeSeed,
    }, linkView);
    if (missing.length) console.error('Clean start: no setter for', missing);

    visitAreaRef.current = null;
    guestArticlesLoadedRef.current = false;
    // Load More: forget the previous account's request and limits; an answer still in flight is
    // dropped because it no longer matches the ref (Advisor SA-1).
    feedPagingRef.current = freshFeedPaging();
    loadingMoreRef.current = false;
    stampContext('locality'); // in-flight group loads now fail their stale-context check

    if (kind === 'signed-out') {
      setShowLoginModal(false);
      setPendingNavigation(null);
      // The one sign-out reload (handleLogout no longer dispatches its own): deferred so the
      // refresh-locations listener has re-registered with the locality context and no user.
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('ournet:refresh-locations', {
          detail: { userId: null }
        }));
      }, 100);
    }
  };

  // ============================================================================
  // SUPABASE AUTH STATE LISTENER
  // ============================================================================

  // Listen for Supabase Auth state changes (login, logout, token refresh)
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔐 Auth state changed:', event, session?.user?.email);

        if (event === 'SIGNED_IN' && session?.user) {
          // Before anything else: the previous account's content goes now, not after the profile
          // round-trip (addendum 1, ruling 3).
          resetForIdentityChange('signed-in', session.user.id);

          // Fetch profile data from public.users (use supabaseFetch to avoid hanging)
          const { data: profileData } = await supabaseFetch.query('users', {
            select: 'display_name,avatar_url,is_sys_admin',
            eq: { id: session.user.id },
            single: true,
            schema: 'public' // the viewer's own account, even while viewing a demo
          });

          setCurrentUser({
            id: session.user.id,
            email: session.user.email,
            display_name: profileData?.display_name ||
                          session.user.user_metadata?.display_name ||
                          session.user.email,
            avatar_url: profileData?.avatar_url || null,
            is_sys_admin: profileData?.is_sys_admin === true
          });
          setIsLoggedIn(true);
          setShowLoginModal(false);
          setDemoSessionExpired(false); // a live session exists again; the notice has done its job

          // Demo mode belongs to the user who entered it: a different user signing in leaves it.
          // Signing in at the demo gate (not yet in demo mode, still on /demo/<slug>) re-runs the
          // route so the gate is checked for the new session.
          if (isDemoReadActive() && session.user.id !== demoUserIdRef.current) {
            leaveDemoMode();
          } else if (demoGateLoginRef.current && /^\/demo\/[^/]+$/.test(window.location.pathname)) {
            demoGateLoginRef.current = false;
            setDemoRouteCheck(n => n + 1);
          }

          // Clear guest articles flag - authenticated users need content cleared on context switch
          guestArticlesLoadedRef.current = false;

          // Refresh user areas after login - include userId so HeaderButtonsFeed can use it immediately
          // (React state propagation may not have completed yet)
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('ournet:refresh-locations', {
              detail: { userId: session.user.id }
            }));
          }, 100);
        } else if (event === 'SIGNED_OUT') {
          resetForIdentityChange('signed-out');
          setCurrentUser(null);
          setIsLoggedIn(false);
          leaveDemoMode(); // demo tables are admin-only; nothing on a demo page survives sign-out
        } else if (event === 'PASSWORD_RECOVERY') {
          console.log('🔐 Password recovery event - showing reset form');
          setShowResetPasswordModal(true);
          // Clean up the URL (remove hash tokens and /auth/reset-password path)
          window.history.replaceState(null, '', '/');
        } else if (event === 'TOKEN_REFRESHED') {
          console.log('🔐 Token refreshed');
        } else if (event === 'USER_UPDATED' && session?.user) {
          // Refresh user data (use supabaseFetch to avoid hanging)
          const { data: profileData } = await supabaseFetch.query('users', {
            select: 'display_name,avatar_url',
            eq: { id: session.user.id },
            single: true,
            schema: 'public' // the viewer's own account, even while viewing a demo
          });

          setCurrentUser(prev => ({
            ...prev,
            display_name: profileData?.display_name || prev?.display_name,
            avatar_url: profileData?.avatar_url || prev?.avatar_url
          }));
        }
      }
    );

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- resetForIdentityChange reads only refs and setters

  // ============================================================================
  // HASH ROUTING UTILITIES
  // ============================================================================

  // Parse the URL hash and return navigation info
  const parseHash = () => {
    const hash = window.location.hash;
    if (!hash || hash === '#' || hash === '#/') {
      return { type: 'feed' };
    }

    // Check for admin routes: #/admin/analytics
    if (hash === '#/admin/analytics') {
      return { type: 'admin-analytics' };
    }

    // Handle password reset callback (must check before generic auth-callback)
    // Supabase redirects with: #/auth/reset-password#access_token=...&type=recovery
    // or sometimes: #access_token=...&type=recovery
    if (hash.startsWith('#/auth/reset-password') || hash.includes('type=recovery')) {
      return { type: 'password-reset' };
    }

    // Handle auth callback (email confirmation redirect from Supabase). A failed link (expired or
    // already used) lands as "#error=…&error_code=…" with no tokens: handled by the same case,
    // which explains it (#88, Advisor A8).
    if (hash.startsWith('#/auth/callback') || hash.includes('access_token=') || hash.startsWith('#error=')) {
      return { type: 'auth-callback' };
    }

    // Invitation URLs: #/invite/{token} (personal), #/join/{token} (shared join link) and the
    // invitation email's #/invitation-optout/{token} and #/invitation-report/{token}. Every token is
    // a signed capability, hex(payload).hex(signature); one shape rule for all four, the one the
    // sign-up carrier uses (isInvitationTokenShape), so the carrier and the routes cannot disagree.
    // These come before the generic matcher below, whose \w cannot match "invitation-optout".
    // Terminal: a cut-off or mangled token is still an invitation page, with token null, so the
    // page explains that the link isn't valid (brief §1) instead of falling to the generic matcher
    // (which rendered nothing for #/join/ and logged the address-bearing fragment as an id).
    const invitationMatch = hash.match(/^#\/(invite|join|invitation-optout|invitation-report)\/(.*)$/);
    if (invitationMatch) {
      return { type: invitationMatch[1], token: isInvitationTokenShape(invitationMatch[2]) ? invitationMatch[2] : null };
    }

    // Expected formats: #/article/{id}, #/chat/{id}, #/event/{id}, #/group/{id}, #/profile/{id}
    const match = hash.match(/^#\/(\w+)\/(.+)$/);
    if (match) {
      return { type: match[1], id: match[2] };
    }

    return { type: 'feed' };
  };

  // Update the URL hash without triggering navigation (used when we navigate internally)
  const updateHash = (type, id = null) => {
    const newHash = id ? `#/${type}/${id}` : '#/';
    if (window.location.hash !== newHash) {
      // Use replaceState for internal navigations to avoid duplicate history entries
      window.history.pushState(null, '', newHash);
    }
  };

  // Clear the hash (return to feed)
  const clearHash = () => {
    if (window.location.hash) {
      window.history.pushState(null, '', window.location.pathname);
    }
  };

  // Leaving an invitation page: its token must not stay in the address, or a reload reopens it.
  // Replaced rather than pushed, so Back does not reopen it either (Advisor, pre-commit S1).
  const clearInvitationHash = () => {
    if (/^#\/(invite|join|invitation-optout|invitation-report)\//.test(window.location.hash)) {
      setInviteToken(null);
      setJoinToken(null);
      setEmailActionToken(null);
      window.history.replaceState(null, '', window.location.pathname);
    }
  };

  // The invitation pages' sign-in / create-account buttons (the invitation rides the confirmation
  // link through LoginRegisterModal's inviteContext).
  const showInvitationLogin = (message, mode) => {
    setAuthRequiredMessage(message);
    setLoginModalMode(mode === 'register' ? 'register' : 'login');
    setShowLoginModal(true);
  };

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  useEffect(() => {
    loadInitialData();
  }, []);

  // Initialize analytics tracking
  useEffect(() => {
    analytics.init();
  }, []);

  // Handle smart links on mount (detect /l/{slug} URLs)
  useEffect(() => {
    const path = window.location.pathname;
    const smartLinkMatch = path.match(/^\/l\/(.+)$/);

    if (smartLinkMatch && !loading) {
      const slug = smartLinkMatch[1];
      console.log('🔗 Smart link detected on mount:', slug);
      handleSmartLink(slug);
    }
  }, [loading]);

  // Handle demo group routes (detect /demo/{slug} URLs)
  useEffect(() => {
    const checkDemoRoute = () => {
      const path = window.location.pathname;

      // Demo group route. /demo/<slug> is the only demo path; the old self-service
      // /demo/<slug>/delete/<token> form was retired in Phase B3 (demos live in the `demo` schema,
      // which no token route can reach). Any other /demo/… path is tidied back to / so it cannot
      // strand the page on an empty feed.
      const demoMatch = path.match(/^\/demo\/([^/]+)$/);
      if (demoMatch && !loading) {
        const demoSlug = demoMatch[1];
        console.log('🎭 Demo group detected:', demoSlug);
        handleDemoGroup(demoSlug);
      } else if (!loading && /^\/demo\//.test(path)) {
        window.history.replaceState(null, '', '/' + window.location.hash);
      }
    };

    // Check on mount and when loading changes
    checkDemoRoute();

    // Also check when pathname changes (e.g., clicking from admin panel)
    const handlePopState = () => {
      console.log('🔄 Pathname changed, checking for demo route');
      checkDemoRoute();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [loading, demoRouteCheck]); // demoRouteCheck: re-run after signing in at the demo gate

  // Handle authority verification routes (detect /verify-authority/{token} URLs)
  useEffect(() => {
    if (loading) return;
    const path = window.location.pathname;
    const verifyMatch = path.match(/^\/verify-authority\/([a-f0-9]{64})$/i);
    if (verifyMatch) {
      console.log('🏛️ Authority verification route detected');
      setAuthorityVerifyToken(verifyMatch[1]);
      setCurrentView('authority-verify');
    }
  }, [loading]);

  // Handle the public directory-suggestion route (detect /suggest — no account)
  useEffect(() => {
    if (loading) return;
    if (window.location.pathname === '/suggest') {
      console.log('📇 Public suggest route detected');
      setCurrentView('suggest');
    }
  }, [loading]);

  // Handle initial hash navigation after data loads
  useEffect(() => {
    if (!loading) {
      const hashNav = parseHash();
      if (hashNav.type === 'password-reset') {
        console.log('🔗 Processing password-reset hash on load');
        handleHashNavigation(hashNav);
      } else if (hashNav.type === 'auth-callback') {
        // Email-confirmation links open a fresh page carrying an id-less hash, so — like
        // password-reset above — this must be routed explicitly or the tokens are never processed.
        console.log('🔗 Processing auth-callback hash on load');
        handleHashNavigation(hashNav);
      } else if (INVITATION_HASH_TYPES.includes(hashNav.type)) {
        // An invitation page opened fresh (an emailed or pasted link). It carries a token, not an
        // id, so the branch below never routed it — the page stayed on the feed (found in
        // Invitations Phase 3). Not logged: a personal token carries the invited address.
        handleHashNavigation(hashNav);
      } else if (hashNav.type !== 'feed' && hashNav.id) {
        console.log('🔗 Processing URL hash:', hashNav);
        handleHashNavigation(hashNav);
      }
    }
  }, [loading]);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handleHashChange = () => {
      const hashNav = parseHash();
      // The type only: an invitation token carries the invited address.
      console.log('🔙 Hash changed (browser navigation):', hashNav.type);

      if (hashNav.type === 'feed') {
        // Going back to feed
        setCurrentView('feed');
        setSelectedArticle(null);
        setSelectedChat(null);
        setSelectedEvent(null);
        setInviteToken(null);
        setJoinToken(null);
        setEmailActionToken(null);
        setFrontPageOpenChatId(null);
      } else if (INVITATION_HASH_TYPES.includes(hashNav.type)) {
        handleHashNavigation(hashNav);
      } else if (hashNav.type === 'admin-analytics') {
        handleHashNavigation(hashNav);
      } else if (hashNav.id) {
        handleHashNavigation(hashNav);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // SW → client navigation bridge (Notifications Commit 2): a push
  // notificationclick posts { type:'navigate', hash } to the focused tab; the
  // bridge sets window.location.hash, which the hashchange handler above turns
  // into a real deep-link navigation. Idempotent; installed once on mount.
  useEffect(() => {
    initServiceWorkerNavBridge();
  }, []);

  // Navigate based on hash - used for initial load and browser back/forward
  const handleHashNavigation = async (hashNav) => {
    const { type, id, token } = hashNav;

    // A deep link to anything not read from `demo` (e.g. a notification) ends demo mode first.
    if (id) leaveDemoUnlessDemoId(id);

    switch (type) {
      case 'article':
        await handleArticleClick(id, false); // false = don't update URL again
        break;
      case 'chat':
        // For locality chats, auto-expand on the front page accordion instead of ChatDetail
        try {
          const chatData = await chatService.getChatById(id);
          if (chatData) {
            if (!chatData.community_id) {
              // Locality chat → front-page accordion auto-expand
              setCurrentView('feed');
              setFrontPageOpenChatId(id);
            } else {
              // Group chat → still use ChatDetail
              handleChatClick(chatData, false);
            }
          } else {
            console.error('❌ Chat not found:', id);
            clearHash();
          }
        } catch (error) {
          console.error('❌ Error loading chat from hash:', error);
          clearHash();
        }
        break;
      case 'event':
        await handleEventClick(id, false);
        break;
      case 'group':
        // Navigate to group context
        try {
          const community = await communityService.getCommunityById(id);
          if (community) {
            setCurrentContext('groups');
            setSelectedGroup(community);
            setCurrentMode('news');
            setCurrentView('feed');
            clearHash(); // Groups don't maintain hash in feed view
          }
        } catch (error) {
          console.error('❌ Error loading group from hash:', error);
          clearHash();
        }
        break;

      case 'group_requests':
        // Notification deep-link to a group's pending-requests view (AdminGroup, where
        // getPendingJoinRequests already renders) — same lookup shape as the 'group' case above.
        try {
          const community = await communityService.getCommunityById(id);
          if (community) {
            handleShowAdminGroup(community, false);
          }
          clearHash();
        } catch (error) {
          console.error('❌ Error loading group for pending requests:', error);
          clearHash();
        }
        break;
      case 'group_settings':
        // Notification deep-link to a group's settings (the group-type check, addendum 5 R4) —
        // same lookup shape as 'group_requests'. The edit form's own permission handling covers a
        // reader who is no longer the owner.
        try {
          const community = await communityService.getCommunityById(id);
          if (community) {
            handleShowNewGroup('edit', community);
          }
          clearHash();
        } catch (error) {
          console.error('❌ Error loading group for settings:', error);
          clearHash();
        }
        break;
      case 'invite':
        // Personal invitation. The token carries the invited address: never logged.
        setInviteToken(token);
        setCurrentView('accept-invitation');
        break;
      case 'join':
        setJoinToken(token);
        setCurrentView('join-group-link');
        break;
      case 'invitation-optout':
      case 'invitation-report':
        setEmailActionToken(token);
        setCurrentView(type);
        break;
      case 'admin-analytics':
        // Navigate to admin analytics dashboard
        console.log('📊 Opening admin analytics');
        setCurrentView('admin-analytics');
        break;
      case 'auth-callback':
        // Email-confirmation callback. detectSessionInUrl is false globally
        // (src/lib/protectedSupabase.js), so — as in the password-reset case below — the tokens must
        // be extracted from the URL fragment by hand and the session established with setSession.
        // The existing onAuthStateChange SIGNED_IN listener then sets the user, closes the login
        // modal and dispatches ournet:refresh-locations, and the home-area self-healer runs as on
        // any first sign-in. Only email-link flows that legitimately carry a session are acted on;
        // the token-bearing URL is replaced on every path so it is not left in history.
        console.log('🔐 Auth callback received - processing confirmation tokens');
        try {
          const rawHash = window.location.hash;
          // Tokens appear after the last '#' — handle both
          // #/auth/callback#access_token=… and #access_token=…
          const tokenFragment = rawHash.includes('#access_token=')
            ? rawHash.substring(rawHash.indexOf('#access_token=') + 1)
            : rawHash.substring(1); // strip leading '#'
          const params = new URLSearchParams(tokenFragment);
          const accessToken = params.get('access_token');
          const refreshToken = params.get('refresh_token');
          const callbackType = params.get('type');
          // Signup confirmation only — the one email-link flow this app issues that should sign the
          // user in. Anything else carrying tokens is stripped from the URL without a session.
          if (accessToken && refreshToken && callbackType === 'signup') {
            console.log('🔐 Confirmation tokens found - establishing session via setSession');
            const { error } = await supabaseRaw.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });
            if (error) {
              console.error('🔐 setSession failed for auth callback:', error.message);
            } else {
              // 2026-09-16 (stale-session-guard brief item 3): acknowledge the sign-in visibly.
              // Deliberately set HERE and not on every SIGNED_IN — this is the one transition
              // with no other visible cue. An ordinary login already announces itself by closing
              // the login modal; a confirmation-link arrival announces nothing at all, which is
              // what made the silent failure indistinguishable from success. The self-healer may
              // replace this with a failure status below if either write then fails.
              setSignInNotice('signed-in');
            }
          } else if (accessToken || refreshToken) {
            console.error('🔐 Auth callback tokens incomplete or of an unexpected type:', callbackType);
          } else if (parseCallbackError(rawHash)) {
            // An expired or already-used link (Supabase lands with "error_code=…" instead of
            // tokens): say so, rather than landing silently on the front page (Advisor A8).
            setSignInNotice('link-failed');
          }
        } catch (err) {
          console.error('🔐 Error processing auth callback tokens:', err);
        } finally {
          // The invitation that rode in the confirmation link (Invitations Build 2 §1, built in
          // #88): read before the address is cleared. A personal invitation reopens its own page,
          // #/invite/<token> — the address an ordinary invitation link shows anyway, so a reload
          // keeps it (Advisor SUGGEST). A shared join link is handed to Invitations Phase 3 the
          // same way, as #/join/<token> in the address — nothing is held in memory for it (A17).
          const carrier = parseCallbackCarrier(window.location.hash);
          window.history.replaceState(null, '',
            carrier ? `/#/${carrier.type === 'join' ? 'join' : 'invite'}/${carrier.token}` : '/');
          // 2026-09-16 (stale-session-guard brief item 2): set the landing destination
          // EXPLICITLY. This case previously never called setCurrentView, so where a confirmed
          // user ended up depended entirely on the home-area self-healer's side effect firing —
          // which is exactly what the stale guard (fixed above) was silently preventing. With
          // the guard fixed this would usually resolve itself, but "usually" is the problem:
          // if home-area creation or the bio write fails for any other reason in future,
          // landing must not fail with it.
          //
          // 2026-10-03 (#88): no later override any more — the old self-healer that sent a new
          // member to My Locations is retired; a member with no home is shown AccountSetup over
          // whatever is underneath. A personal invitation opens first (R5): joining does not wait
          // for setup, and the setup gate stands aside on that page.
          if (carrier?.type === 'invite') {
            setInviteToken(carrier.token);
            setCurrentView('accept-invitation');
          } else if (carrier?.type === 'join') {
            // A shared join link opens first too (Invitations Phase 3): replaceState above fires no
            // hashchange, so the view is set here, not left to the address.
            setJoinToken(carrier.token);
            setCurrentView('join-group-link');
          } else {
            setCurrentView('feed');
          }
        }
        break;
      case 'password-reset':
        // Password reset callback — detectSessionInUrl is false globally, so we must
        // manually extract the recovery tokens and establish the session ourselves.
        // setSession fires SIGNED_IN (not PASSWORD_RECOVERY), so we show the modal directly.
        console.log('🔐 Password reset callback received - manually processing recovery tokens');
        try {
          const rawHash = window.location.hash;
          // Tokens appear after the last '#' — handle both
          // #/auth/reset-password#access_token=… and #access_token=…
          const tokenFragment = rawHash.includes('#access_token=')
            ? rawHash.substring(rawHash.indexOf('#access_token=') + 1)
            : rawHash.substring(1); // strip leading '#'
          const params = new URLSearchParams(tokenFragment);
          const accessToken = params.get('access_token');
          const refreshToken = params.get('refresh_token');

          if (accessToken && refreshToken) {
            console.log('🔐 Recovery tokens found - establishing session via setSession');
            const { error } = await supabaseRaw.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });
            if (error) {
              console.error('🔐 setSession failed for recovery:', error.message);
            }
            // Show the modal directly — setSession fires SIGNED_IN (not
            // PASSWORD_RECOVERY) when detectSessionInUrl is false, so the
            // event-driven branch never triggers. We already know this is a
            // recovery flow from parseHash(), so raise the modal ourselves.
            setShowResetPasswordModal(true);
            window.history.replaceState(null, '', '/');
          } else {
            console.error('🔐 Recovery hash missing access_token or refresh_token');
          }
        } catch (err) {
          console.error('🔐 Error processing recovery tokens:', err);
        }
        break;
      default:
        console.warn('⚠️ Unknown hash type:', type);
        clearHash();
    }
  };

  // Resolves the default guest locality from IP geolocation —
  // without touching any component state. Shared by the cold guest load (loadInitialData) and
  // the guest branch of handleLocationsRefresh (fires on sign-out via ournet:refresh-locations),
  // so a guest boundary is derived exactly one way regardless of how a guest arrived at "no
  // session" — see docs/briefs/2026-08-13-guest-boundary-lost-on-signout.md.
  const resolveGuestDefaultLocation = async () => {
    const result = {
      name: null,
      lat: null,
      lon: null,
      boundaryId: null,
      homeBoundaryIds: null,   // boundary set for feed queries
      guestCoords: null,       // raw IP lat/lng — only set when IP supplied the display name
      countryCode: null
    };

    // IP geolocation — also populates the ournet_ip_location cache (used by MapLocationPicker /
    // guest browse)
    try {
      const ipLocation = await getLocationFromIP();

      if (ipLocation) {
        console.log('🌍 IP location detected:', ipLocation.city, ipLocation.country);
        result.countryCode = ipLocation.countryCode || null;

        const locality = await reverseGeocodeToLocality(
          ipLocation.latitude,
          ipLocation.longitude
        );

        if (locality && locality.distance <= 50000) {
          console.log(`✅ Nearest locality: ${locality.name} (${(locality.distance / 1000).toFixed(1)}km away)`);
          result.name = locality.name;
          result.lat = locality.latitude;
          result.lon = locality.longitude;
          result.guestCoords = { lat: ipLocation.latitude, lng: ipLocation.longitude };
        } else if (locality) {
          console.log(`⚠️ IP location outside served area (${(locality.distance / 1000).toFixed(1)}km from ${locality.name}) - showing all public articles`);
        } else {
          console.log('⚠️ No locality found near IP location - showing all public articles');
        }

        // Resolve coordinates to admin_boundaries id and cache for registration inheritance (Piece 0)
        try {
          const { data: boundaryId, error: resolveError } = await supabaseFetch.rpc(
            'resolve_point_to_boundary',
            { p_lat: ipLocation.latitude, p_lng: ipLocation.longitude }
          );
          if (!resolveError && boundaryId) {
            console.log(`✅ Guest IP boundary resolved: ${boundaryId}`);
            result.boundaryId = boundaryId;
            result.homeBoundaryIds = [boundaryId];
            // Extend the localStorage cache with the resolved boundary id + country code
            try {
              const cached = localStorage.getItem('ournet_ip_location');
              if (cached) {
                const cacheObj = JSON.parse(cached);
                cacheObj.data.boundaryId = boundaryId;
                if (ipLocation.countryCode) {
                  cacheObj.data.countryCode = ipLocation.countryCode;
                }
                localStorage.setItem('ournet_ip_location', JSON.stringify(cacheObj));
              }
            } catch (e) {
              // Cache update failed — non-fatal
            }
          } else {
            console.log('⚠️ Guest boundary resolution returned null (coords outside served boundaries)');
          }
        } catch (resolveErr) {
          console.warn('⚠️ Guest boundary resolution error:', resolveErr.message);
        }
      } else {
        console.log('⚠️ IP geolocation failed - showing all public articles');
      }
    } catch (ipError) {
      console.warn('⚠️ IP geolocation error:', ipError.message);
    }

    return result;
  };

  const loadInitialData = async () => {
    setLoading(true);
    // Stamp: initial load is always the locality feed (demo/smartlink loaders re-stamp).
    stampContext('locality');
    const myContext = 'locality';

    try {
      console.log('📡 Loading initial data from database...');

      // 1. Load communities
      const communitiesData = await communityService.getAllCommunities();
      console.log('✅ Loaded communities:', communitiesData.length);

      if (communitiesData.length > 0) {

        // 2. Load authenticated user via Supabase Auth session
        let userId = null;
        let isUserAuthenticated = false; // Local flag (React state is async)

        // Helper function to add timeout to promises (Supabase client may hang)
        const withTimeout = (promise, timeoutMs, fallbackValue) => {
          return Promise.race([
            promise,
            new Promise((resolve) =>
              setTimeout(() => {
                console.warn(`⏱️ Query timed out after ${timeoutMs}ms, using fallback`);
                resolve(fallbackValue);
              }, timeoutMs)
            )
          ]);
        };

        try {
          // Check for existing auth session
          // BYPASS: Supabase auth.getSession() hangs - check storage directly
          // Uses authStorage adapter (IndexedDB + localStorage) for Android persistence
          console.log('🔐 Checking auth session via authStorage...');

          let session = null;
          let hasStoredSession = false;

          // Derive the Supabase storage key from the project URL
          const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
          const projectRef = supabaseUrl?.split('//')[1]?.split('.')[0];
          const authStorageKey = `sb-${projectRef}-auth-token`;

          // Read from authStorage (IndexedDB first, localStorage fallback)
          // Also proactively refresh expired tokens so mobile sessions survive
          try {
            const raw = await authStorage.getItem(authStorageKey);
            if (raw) {
              // Use getAuthToken() which auto-refreshes expired tokens
              const freshToken = await supabaseFetch.getAuthToken();
              // Re-read after potential refresh
              const refreshedRaw = await authStorage.getItem(authStorageKey);
              const storedData = refreshedRaw ? JSON.parse(refreshedRaw) : {};
              if (freshToken && storedData?.user) {
                console.log('🔐 Found stored session for:', storedData.user.email);
                session = {
                  access_token: freshToken,
                  user: storedData.user
                };
                hasStoredSession = true;
              } else if (storedData?.access_token && storedData?.user) {
                // Fallback: getAuthToken failed but we still have stored data
                console.log('🔐 Using stored session (refresh unavailable) for:', storedData.user.email);
                session = {
                  access_token: storedData.access_token,
                  user: storedData.user
                };
                hasStoredSession = true;
              }
            }
          } catch (storageErr) {
            console.warn('🔐 Could not read auth from storage:', storageErr.message);
          }

          // If no session found in storage, wait briefly for Supabase client
          // to restore from IndexedDB (race condition fix for Android)
          if (!hasStoredSession) {
            console.log('🔐 No immediate session, waiting for Supabase client restore...');
            const restored = await Promise.race([
              new Promise((resolve) => {
                const { data: { subscription } } = supabase.auth.onAuthStateChange(
                  (event, sess) => {
                    if (event === 'SIGNED_IN' && sess?.user) {
                      subscription.unsubscribe();
                      resolve(sess);
                    }
                  }
                );
                // Also check if Supabase already has the session (event may have fired)
                // Re-read storage in case Supabase wrote it during our wait
                authStorage.getItem(authStorageKey).then((raw2) => {
                  if (raw2) {
                    const d = JSON.parse(raw2);
                    if (d?.access_token && d?.user) {
                      subscription.unsubscribe();
                      resolve({ access_token: d.access_token, user: d.user });
                    }
                  }
                }).catch(() => {});
              }),
              new Promise((resolve) => setTimeout(() => resolve(null), 2000))
            ]);

            if (restored?.user) {
              console.log('🔐 Session restored from Supabase client for:', restored.user.email);
              session = restored;
              hasStoredSession = true;
            }
          }

          if (!hasStoredSession) {
            console.log('🔐 No stored session found, proceeding as guest');
          }

          if (session?.user) {
            userId = session.user.id;
            isUserAuthenticated = true; // Set local flag immediately

            // Fetch profile data using supabaseFetch (reliable)
            const { data: profileData } = await supabaseFetch.query('users', {
              select: 'display_name, avatar_url, bio, is_sys_admin',
              eq: { id: userId },
              single: true,
              schema: 'public' // the viewer's own account, even while viewing a demo
            });

            setCurrentUser({
              id: userId,
              email: session.user.email,
              display_name: profileData?.display_name ||
                            session.user.user_metadata?.display_name ||
                            session.user.email,
              avatar_url: profileData?.avatar_url || null,
              is_sys_admin: profileData?.is_sys_admin === true
            });
            setIsLoggedIn(true);
            console.log('✅ Authenticated user loaded:', session.user.email);
          } else {
            // No active session - guest mode (read-only browsing)
            // Note: isLoggedIn stays FALSE - guests cannot make mutations
            userId = null; // No user ID for guests
            console.log('👤 Guest mode - login required to contribute');

            setCurrentUser({
              id: null,
              display_name: 'Guest',
              email: null,
              is_sys_admin: false
            });

            // Clean up any old credential storage
            localStorage.removeItem('ournet_saved_password');
          }
        } catch (err) {
          console.warn('⚠️ Failed to get authenticated user, using guest mode:', err);
          userId = null;
          setCurrentUser({
            id: null,
            display_name: 'Guest',
            email: null,
            is_sys_admin: false
          });
        }

        // 3. Get user's home area for geocentric content filtering (NEW - MVP Geocentric)
        let userHomeAreaId = null;
        let homeBoundaryIds = null; // boundary_ids from user's home area (new location system)
        let guestCoordsLocal = null; // IP-detected coordinates for feed queries

        // Fetch user's home area from user_areas (using direct fetch - Supabase client unreliable)
        // Guard: only query when userId is a real UUID — passing null produces user_id=eq.null
        // which PostgREST treats as matching the string "null", not SQL NULL.
        try {
          if (userId) {
            console.log('📍 Loading user home area for geocentric filtering...');
            const { data: homeArea, error: homeAreaError } = await supabaseFetch.query('user_areas', {
              select: 'id,area_code,display_name,boundary_ids,primary_boundary_id',
              eq: { user_id: userId, is_home: true },
              single: true
            });

            if (homeAreaError) {
              console.warn('⚠️ Error fetching home area:', homeAreaError.message);
            }

            if (homeArea) {
              userHomeAreaId = homeArea.id;
              homeBoundaryIds = homeArea.boundary_ids || [];

              // Resolve selectedArea on session-restore, not just on interactive login/logout
              // (docs/briefs/2026-09-01-logged-in-session-restore-directory-image-fix.md). A plain
              // reload of an already-authenticated session never fires ournet:refresh-locations
              // (AuthContext.jsx's handleLoginSuccess/handleLogout are the only two dispatch sites),
              // so useDirectoryListings' boundaryId — which reads selectedArea first — fell through
              // to the guest-only fallback and stayed null. This also carries the header locality
              // name fix from addendum 1: HeaderButtonsFeed's own loadUserAreas effect (keyed on
              // selectedArea?.id) re-fires once this resolves and calls loadAreaLocalities with the
              // now-known area, which is what actually sets HeaderButtonsFeed's local
              // homeLocalityName state the header renders — not App.js's own userHomeLocationName,
              // which HeaderButtonsFeed never receives as a prop.
              // Guarded with the functional-update form (prev ?? ...), not a blind overwrite: this
              // write only resolves after two awaited round-trips (auth session, then this
              // home-area query), so an interactive area-switch (ournet:area-changed ->
              // handleAreaChanged, or a fresh login's ournet:refresh-locations ->
              // handleLocationsRefresh) could plausibly land first on a slow connection. If it has,
              // selectedArea is already non-null and this becomes a no-op, so a deliberate switch
              // is never clobbered back to home — same stale-write hazard the activeContextRef/
              // currentViewRef guards elsewhere in this file exist for (Architecture.md's
              // documented stale-read idiom), applied here via React's own prev-value check instead
              // of a separate ref.
              setSelectedArea(prev => prev ?? {
                id: homeArea.id,
                area_code: homeArea.area_code,
                display_name: homeArea.display_name,
                boundary_ids: homeBoundaryIds,
                primary_boundary_id: homeArea.primary_boundary_id || null
              });

              // Guaranteed baseline for the OTHER real userHomeLocationName consumers (ChatDetail,
              // DirectoryColumn, LocalityFrontPage, MobileFrontPage, SidebarLocality,
              // SidebarContextWidget — HeaderButtonsFeed is not among them, see above). Whatever the
              // centroid RPC below does (succeed, return empty, or throw — its own try/catch
              // swallows a throw into the outer catch with no retry), this guarantees a real name
              // first, matching the same homeArea.display_name fallback already used twice further
              // down in this function. The centroid RPC, if it succeeds, still refines this to the
              // nicer resolved name.
              setUserHomeLocationName(homeArea.display_name);

              if (homeBoundaryIds.length > 0) {
                // New system: derive display name and coords from boundary centroid
                const { data: centroid } = await supabaseFetch.rpc('get_boundary_centroid', {
                  p_boundary_ids: headlineBoundaryIds(homeArea)
                });
                if (centroid?.[0]) {
                  setUserHomeLocationName(centroid[0].name);
                  setUserHomeLocationCoords({ lat: centroid[0].lat, lon: centroid[0].lon });
                  console.log('✅ Home area (boundary):', centroid[0].name, `(${centroid[0].lat}, ${centroid[0].lon})`);
                } else {
                  setUserHomeLocationName(homeArea.display_name);
                }
              } else {
                // Legacy system: derive coords from user_area_localities → localities
                const { data: primaryLocality, error: localityError } = await supabaseFetch.query('user_area_localities', {
                  select: 'locality:localities(name,latitude,longitude)',
                  eq: { area_id: homeArea.id, display_order: 0 },
                  single: true
                });

                if (localityError) {
                  console.warn('⚠️ Error fetching locality:', localityError.message);
                }

                if (primaryLocality?.locality?.name) {
                  setUserHomeLocationName(primaryLocality.locality.name);
                  if (primaryLocality.locality.latitude && primaryLocality.locality.longitude) {
                    setUserHomeLocationCoords({
                      lat: primaryLocality.locality.latitude,
                      lon: primaryLocality.locality.longitude
                    });
                    console.log('✅ Home area (locality):', primaryLocality.locality.name, homeArea.id);
                  }
                } else {
                  setUserHomeLocationName(homeArea.display_name);
                  console.log('✅ Home area (display name):', homeArea.display_name, homeArea.id);
                }
              }
            } else {
              console.warn('⚠️ No home area found for user');
            }
          } else {
            // Guest user — resolve the default locality from IP geolocation
            console.log('🌍 Guest user detected - resolving default location...');

            const guestLocation = await resolveGuestDefaultLocation();
            if (guestLocation.name) {
              setUserHomeLocationName(guestLocation.name);
            }
            if (guestLocation.lat && guestLocation.lon) {
              setUserHomeLocationCoords({ lat: guestLocation.lat, lon: guestLocation.lon });
            }
            if (guestLocation.boundaryId) {
              setDetectedBoundaryId(guestLocation.boundaryId);
              detectedBoundaryIdRef.current = guestLocation.boundaryId;
            }
            homeBoundaryIds = guestLocation.homeBoundaryIds;
            guestCoordsLocal = guestLocation.guestCoords;
          }
        } catch (err) {
          console.warn('⚠️ Failed to load user home area:', err);
        }

        // 4. Load community content (skip if demo route - demo handler will load group content)
        const currentPath = window.location.pathname;
        const isDemoRoute = currentPath.match(/^\/demo\/([^/]+)$/); // exactly /demo/<slug>
        const isSmartLinkRoute = currentPath.match(/^\/l\/(.+)$/);

        if (isDemoRoute) {
          console.log('🎭 Demo route detected - skipping initial locality content load');
          // Leave articles/chats/events empty - demo handler will load group content
          setArticlesWithMeta([]);
          setChats([]);
          setEventsWithMeta([]);
        } else if (isSmartLinkRoute) {
          console.log('🔗 Smart link route detected - skipping initial locality content load');
          // Leave empty - smart link handler will load target content
          setArticlesWithMeta([]);
          setChats([]);
          setEventsWithMeta([]);
        } else {
          // Normal locality route - load locality content
          // An active visit takes precedence over home
          const feedBoundaryIds = resolveScopeBoundaryIds({
            fallbackBoundaryIds: homeBoundaryIds,
            visitBoundaryIds: visitArea?.boundaryIds
          });

          const [articlesData, chatsData, eventsData] = await Promise.all([
            fetchLocalityNews(userHomeAreaId, { boundaryIds: feedBoundaryIds, guestCoords: guestCoordsLocal }),
            chatService.getLocalityChats(feedBoundaryIds),
            fetchLocalityEvents(userHomeAreaId, { boundaryIds: feedBoundaryIds, guestCoords: guestCoordsLocal })
          ]);

          if (activeContextRef.current !== myContext) return; // stale — user navigated away
          setArticlesWithMeta(articlesData);
          setChats(chatsData);
          setEventsWithMeta(eventsData);

          console.log('✅ Content loaded - Articles:', articlesData.length, 'Chats:', chatsData.length, 'Events:', eventsData.length);
        }

        // Mark that guest articles have been loaded to prevent clearing
        if (!isUserAuthenticated) {
          guestArticlesLoadedRef.current = true;
        }

      } else {
        console.warn('⚠️ No communities found in database');
        setArticlesWithMeta([]);
        setChats([]);
        setEventsWithMeta([]);
      }

    } catch (error) {
      console.error('❌ Error loading initial data:', error);

      // Fallback to prevent crash
      setArticlesWithMeta([]);
      setChats([]);
      setEventsWithMeta([]);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // SMART LINK HANDLER
  // ============================================================================

  // Handle smart link resolution when user visits /l/{slug}
  const handleSmartLink = async (slug) => {
    console.log('🔗 Processing smart link:', slug);
    setSmartLinkLoading(true);

    try {
      // Look up the smart link
      const linkData = await smartLinkService.getSmartLinkBySlug(slug);

      if (!linkData) {
        console.error('❌ Smart link not found:', slug);
        showAppModal('This link is not valid or has been removed.');
        setSmartLinkLoading(false);
        return;
      }

      console.log('🔗 Smart link found:', linkData);
      setSmartLinkData(linkData);

      // Check if link is usable
      if (!linkData.is_usable) {
        console.warn('⚠️ Smart link is not usable:', linkData);
        // Show preview with appropriate error message
        setSmartLinkTarget({ name: 'Link Unavailable', description: 'This link is no longer active.' });
        setShowSmartLinkPreview(true);
        setSmartLinkLoading(false);
        return;
      }

      // Fetch the target entity based on type
      let targetData = null;

      if (linkData.target_type === 'community' || linkData.target_type === 'group') {
        // Handle group/community links
        targetData = await communityService.getCommunityById(linkData.target_id);

        if (!targetData) {
          console.error('❌ Smart link target not found:', linkData.target_id);
          showAppModal('The group or community this link points to no longer exists.');
          setSmartLinkLoading(false);
          return;
        }

        setSmartLinkTarget(targetData);

        // Check if user is already a member
        if (isLoggedIn && currentUser?.id) {
          const membership = await groupService.checkMembership(currentUser.id, targetData.id);

          if (membership.isMember) {
            // User is already a member — go straight into the group, landing on News (members get
            // the content immediately, per the membership-aware landing rule).
            // (Tier 2: the old `setCurrentView('intro')` branch pointed at a view that was never
            // rendered, blanking the screen for groups with a description. That dead view is gone;
            // a member lands on the real News surface. On mobile the gate effect also resolves to
            // News for a member, so this is consistent across viewports.)
            console.log('✅ User is member - opening group on News');
            await smartLinkService.incrementSmartLinkUse(linkData.id);
            setCurrentContext('groups');
            setSelectedGroup(targetData);
            setCurrentMode('news');
            setFrontPageActive(true);
            setCurrentView('feed');
            setSmartLinkLoading(false);
            return;
          }
        }

        // User is not a member or not logged in - show preview
        console.log('👁️ Showing smart link preview');
        setShowSmartLinkPreview(true);
        setSmartLinkLoading(false);

      } else if (linkData.target_type === 'conversation') {
        // Handle article links (articles are stored with target_type='conversation')
        targetData = await articleService.getArticleById(linkData.target_id);

        if (!targetData) {
          console.error('❌ Smart link target article not found:', linkData.target_id);
          showAppModal('The article this link points to no longer exists.');
          setSmartLinkLoading(false);
          return;
        }

        console.log('📰 Article link - navigating directly');
        await smartLinkService.incrementSmartLinkUse(linkData.id);

        // Store article's locality context so guest can see the article's locality feed on back
        smartLinkLocalityRef.current = {
          localityIds: targetData.locality_ids || [],
          lat: targetData.latitude,
          lng: targetData.longitude,
          locationName: null // Will be resolved when loading the feed
        };

        // Navigate to article view using existing handler
        await handleArticleClick(linkData.target_id);
        setSmartLinkLoading(false);

      } else if (linkData.target_type === 'event') {
        // Handle event links
        targetData = await eventService.getEventById(linkData.target_id);

        if (!targetData) {
          console.error('❌ Smart link target event not found:', linkData.target_id);
          showAppModal('The event this link points to no longer exists.');
          setSmartLinkLoading(false);
          return;
        }

        console.log('🎉 Event link - navigating directly');
        await smartLinkService.incrementSmartLinkUse(linkData.id);

        // Navigate to event view using existing handler
        await handleEventClick(linkData.target_id);
        setSmartLinkLoading(false);

      } else if (linkData.target_type === 'user') {
        // Handle user profile links (not yet implemented - graceful message)
        console.warn('👤 User profile links not yet available');
        showAppModal('User profiles are not yet available. This feature is coming soon!');
        setSmartLinkLoading(false);

      } else {
        console.error('❌ Unsupported target type:', linkData.target_type);
        showAppModal('This link type is not currently supported.');
        setSmartLinkLoading(false);
      }
    } catch (error) {
      console.error('❌ Error processing smart link:', error);
      showAppModal('An error occurred while processing this link.');
      setSmartLinkLoading(false);
    }
  };

  // Handle demo group access — appointed demo admins only (Phase B1, 18 Sep 2026).
  // The lookup reads demo.communities explicitly (schema 'demo') — never public's demo_slug, so a
  // public group carrying a colliding slug can never be served here, whatever the call order.
  // No is_demo filter: every row in the demo schema is demo by construction (new rows default
  // is_demo = false). Then the demo read target routes the ordinary group page's reads to `demo`.
  const handleDemoGroup = async (demoSlug) => {
    console.log('🎭 Loading demo group:', demoSlug);

    try {
      // Gate 1 — signed in. Signed out → the existing login modal; the route re-runs on sign-in
      // (demoGateLoginRef tells the auth listener this sign-in came from the gate).
      const authToken = await supabaseFetch.getAuthToken();
      if (!authToken) {
        demoGateLoginRef.current = true;
        setAuthRequiredMessage('gate_body_generic');
        setLoginModalMode('login');
        setShowLoginModal(true);
        return;
      }

      // Gate 1b — a token that is present but dead (expired, undecodable, or a refresh that hit the
      // network on a waking laptop). This check sits BEFORE the gate deliberately (addendum 1,
      // decision 2.3). is_appointed_demo_admin() is a public RPC and keeps its anon fallback under
      // ruling 2; it is revoked from anon and demoReadService maps any error to false, so asking it
      // with a dead token would answer "not appointed" and tell the demo admin the demo does not
      // exist. Checking first means the gate only ever runs with a real token and cannot misreport.
      // No leaveDemoMode(), no "not available" — the notice, with a sign-in action, on this route.
      if (!isUsableAccessToken(authToken)) {
        console.warn('🎭 Demo session is no longer usable — showing the expired-session notice:', demoSlug);
        setDemoSessionExpired(true);
        return;
      }

      // Gate 2 — appointed demo admin. "Not appointed" and "not found" show the same message, so
      // the page never reveals which slugs exist; RLS on every demo table is the real control.
      if (!(await isAppointedDemoAdmin())) {
        console.error('❌ Demo not available (not an appointed demo admin):', demoSlug);
        showAppModal('This demo group is not available.');
        return;
      }

      enterDemoRead();
      const { data: demoGroup, error } = await getDemoGroupBySlug(demoSlug);

      // A session that died between gate 1b and this call. Without this branch the throw below
      // reaches the catch, which calls leaveDemoMode() and says "Failed to load demo group" — the
      // two outcomes ruling 3 forbids, on the one action the notice tells the user to take.
      if (isDemoSessionExpired(error)) {
        console.warn('🎭 Demo session expired during the demo lookup:', demoSlug);
        // exitDemoRead(), NOT leaveDemoMode(). Only the read target was armed a few lines above
        // (isDemoMode is still false, nothing is on screen yet), so disarming it is all that is
        // needed — and leaveDemoMode() is the function ruling 3 forbids for this cause. The URL
        // stays on /demo/<slug> and the user sees the notice, not an ejection.
        exitDemoRead();
        setDemoSessionExpired(true);
        return;
      }

      if (error) throw error;

      if (!demoGroup) {
        console.error('❌ Demo group not found or inactive:', demoSlug);
        leaveDemoMode({ replaceUrl: false });
        showAppModal('This demo group is not available.');
        return;
      }

      // Something (a sign-in by another user, a deep link to live content) may have ended demo
      // mode while the lookup was in flight — don't commit demo state on top of that.
      if (!isDemoReadActive() || !isDemoId(demoGroup.id)) {
        console.warn('🎭 Demo mode ended during the demo lookup — not entering:', demoSlug);
        return;
      }

      console.log('✅ Demo group loaded:', demoGroup.name);

      // Who entered demo mode (JWT sub; the payload is base64url, which atob needs translated).
      try {
        const payload = authToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        demoUserIdRef.current = JSON.parse(atob(payload.padEnd(payload.length + (4 - payload.length % 4) % 4, '='))).sub || null;
      } catch (e) {
        demoUserIdRef.current = null;
      }
      setIsDemoMode(true);
      setDemoSessionExpired(false); // a live demo is on screen; any earlier notice is spent

      // Navigate to group view
      setCurrentContext('groups');
      setSelectedGroup(demoGroup);
      setCurrentMode('news');
      setCurrentView('feed');

      console.log('🎭 Demo state set - context: groups, group:', demoGroup.name);

      // Load demo group content (the loaders stamp 'group:<id>' — a demo id, so demo mode holds)
      console.log('🎭 Loading demo group content...');
      await handleLoadArticlesForGroup(demoGroup.id);
      await handleLoadChatsForGroup(demoGroup.id);
      await handleLoadEventsForGroup(demoGroup.id);
    } catch (error) {
      console.error('❌ Error loading demo group:', error);
      leaveDemoMode({ replaceUrl: false });
      showAppModal('Failed to load demo group.');
    }
  };

  // Handle smart link join action
  const handleSmartLinkJoin = async () => {
    if (!isLoggedIn) {
      // Should not happen (button is hidden), but handle gracefully
      setShowLoginModal(true);
      return;
    }

    if (!smartLinkData || !smartLinkTarget) {
      console.error('❌ Missing smart link data for join');
      return;
    }

    // A federation federates groups, not people (4C §5) — the button that calls this is
    // already suppressed in SmartLinkPreview, this is the belt-and-suspenders check on the
    // actual write path.
    if (smartLinkTarget.is_federation) {
      console.error('❌ Smart link join attempted on a federation group');
      return;
    }

    setSmartLinkLoading(true);

    try {
      // Increment use count
      await smartLinkService.incrementSmartLinkUse(smartLinkData.id);

      // Join the group/community
      const targetId = smartLinkTarget.id;
      const targetName = smartLinkTarget.name;

      // Check if auto-join or requires approval
      if (smartLinkTarget.auto_accept_members) {
        // Auto-join. joinGroup() returns {success, status, message} — checked on .success
        // (not truthy-object) so a failed/blocked join is no longer mistaken for success.
        const result = await groupService.joinGroup(currentUser.id, targetId);

        if (result.success) {
          console.log('✅ Successfully joined via smart link');
          showAppModal(`Welcome to ${targetName}!`);

          // Navigate to the group
          setCurrentContext('groups');
          setSelectedGroup(smartLinkTarget);
          setCurrentMode('news');
          setCurrentView('feed');
          setShowSmartLinkPreview(false);
        } else if (result.status === 'blocked') {
          showAppModal('You are not able to request to join this group.');
        } else {
          showAppModal('Failed to join the group. Please try again.');
        }
      } else {
        // Requires approval - create join request
        const success = await groupService.requestToJoinGroup(
          currentUser.id,
          targetId,
          `Joined via smart link: ${smartLinkData.slug}`
        );

        if (success) {
          showAppModal(`Your request to join ${targetName} has been sent to the group admins.`);
          setShowSmartLinkPreview(false);
          setCurrentView('feed');
        } else {
          showAppModal('Failed to send join request. Please try again.');
        }
      }
    } catch (error) {
      console.error('❌ Error joining via smart link:', error);
      showAppModal('An error occurred while joining. Please try again.');
    } finally {
      setSmartLinkLoading(false);
    }
  };

  // ============================================================================
  // NAVIGATION HANDLERS
  // ============================================================================

  // Helper: Save current feed scroll position before navigating away
  const saveFeedScrollPosition = () => {
    if (currentView === 'feed') {
      const mainContent = document.getElementById('main-feed-content');
      if (mainContent) {
        setFeedScrollPosition(mainContent.scrollTop);
        console.log('💾 Saved feed scroll position:', mainContent.scrollTop);
      }
    }
  };

  // Article Navigation
  const handleArticleClick = async (articleId, updateUrl = true) => {
    leaveDemoUnlessDemoId(articleId); // a live article opened from a demo page ends demo mode
    // Save scroll position before navigating away from feed
    saveFeedScrollPosition();
    console.log('📰 Article clicked:', articleId);
    console.log('📰 articleService exists:', !!articleService);
    console.log('📰 getArticleById exists:', !!articleService?.getArticleById);

    try {
      console.log('📰 Calling getArticleById...');
      const article = await articleService.getArticleById(articleId);
      console.log('📰 getArticleById returned:', article ? 'article found' : 'null');

      if (article) {
        setSelectedArticle(article);
        setCurrentView('article');

        // Track navigation breadcrumb
        window.dispatchEvent(new CustomEvent('ournet:navigate', {
          detail: { view: 'article', articleId }
        }));

        // Update URL hash for sharing
        if (updateUrl) {
          updateHash('article', articleId);
        }

        // Track view count
        await articleService.incrementViewCount(articleId);
        console.log('✅ Article loaded:', article.title);
      } else {
        console.error('❌ Article not found:', articleId);
      }
    } catch (error) {
      console.error('❌ Error loading article:', error);
    }
  };

  // Chat Navigation
  const handleChatClick = async (chat, updateUrl = true) => {
    leaveDemoUnlessDemoId(chat?.id); // a live chat opened from a demo page ends demo mode
    // Save scroll position before navigating away from feed
    saveFeedScrollPosition();

    // Load responses for this chat BEFORE changing view
    let responses = [];
    try {
      responses = await chatService.getThreadChats(chat.id);
    } catch (error) {
      console.error('Error loading chat responses:', error);
    }

    // Set all state together
    setSelectedChat(chat);
    setSelectedChatResponses(responses || []);
    setCurrentView('chat');

    // Track navigation breadcrumb
    window.dispatchEvent(new CustomEvent('ournet:navigate', {
      detail: { view: 'chat', chatId: chat.id }
    }));

    // Update URL hash for sharing
    if (updateUrl) {
      updateHash('chat', chat.id);
    }
  };

  // Front-page chat accordion toggle — keeps hash in sync
  const handleFrontPageChatThreadToggle = (threadId) => {
    setFrontPageOpenChatId(threadId);
    // Hash is managed by ChatColumn itself
  };

  // Reload locality chats (called by ChatColumn after creating a thread or reply). Was never
  // made visit-aware by any Change 3 build — read selectedArea?.boundary_ids (home) directly
  // with no resolver call at all, so posting a chat while visiting overwrote the correct visited
  // list with home's (residual bug after 4958b255, diagnosed 2026-09-02). visitAreaRef.current,
  // not visitArea state, matching this file's established resolver convention.
  const handleLocalityChatsRefresh = async () => {
    const boundaryIds = resolveScopeBoundaryIds({
      fallbackBoundaryIds: selectedArea?.boundary_ids || [],
      visitBoundaryIds: visitAreaRef.current?.boundaryIds
    });
    if (boundaryIds.length > 0) {
      const myContext = 'locality';
      try {
        const updatedChats = await chatService.getLocalityChats(boundaryIds);
        if (activeContextRef.current !== myContext) return; // stale — user navigated away
        setChats(updatedChats || []);
      } catch (error) {
        console.error('Error refreshing locality chats:', error);
      }
    }
  };

  // Group-scoped post-create refresh (Groups Tier 1, Commit 4). The locality refresh above is
  // boundary-scoped and bails in group context, so a new group chat never reappeared without a
  // full reload. This refetches the selected group's chats (community_id-scoped), guarded by the
  // same 'group:'+id key as the initial load so a context switch mid-fetch can't clobber state.
  const handleGroupChatsRefresh = async () => {
    if (!selectedGroup?.id) return;
    const myContext = 'group:' + selectedGroup.id;
    try {
      const updatedChats = await chatService.getOriginalChats(selectedGroup.id);
      if (activeContextRef.current !== myContext) return; // stale — user navigated away
      setChats(updatedChats || []);
    } catch (error) {
      console.error('Error refreshing group chats:', error);
    }
  };

  // Event Navigation
  const handleEventClick = async (eventOrId, updateUrl = true) => {
    // Save scroll position before navigating away from feed
    saveFeedScrollPosition();

    const eventId = typeof eventOrId === 'object' ? eventOrId.id : eventOrId;
    leaveDemoUnlessDemoId(eventId); // a live event opened from a demo page ends demo mode
    console.log('📅 Event clicked:', eventId);

    try {
      // Load full event details including event_details table data
      const eventData = await eventService.getEventById(eventId);

      if (eventData) {
        setSelectedEvent(eventData);
        setCurrentView('event');

        // Track navigation breadcrumb
        window.dispatchEvent(new CustomEvent('ournet:navigate', {
          detail: { view: 'event', eventId }
        }));

        // Update URL hash for sharing
        if (updateUrl) {
          updateHash('event', eventId);
        }

        console.log('✅ Event loaded:', eventData.title);
      } else {
        console.error('❌ Event not found:', eventId);
      }
    } catch (error) {
      console.error('❌ Error loading event:', error);
    }
  };

  // Back to Feed
  const handleBackToFeed = async () => {
    console.log('⬅️ Back to feed, selectedArea:', selectedArea);

    // Clear URL hash when returning to feed
    clearHash();

    // Also clear smart link pathname so browser URL is clean
    if (window.location.pathname.startsWith('/l/')) {
      window.history.replaceState(null, '', '/');
    }

    // Signal MainFeed to restore scroll position
    setShouldRestoreScroll(true);

    setCurrentView('feed');
    setSelectedArticle(null);
    setSelectedChat(null);
    setSelectedEvent(null);
    setFrontPageOpenChatId(null);

    // Guest arriving via smart link: articles array is empty, load the article's locality feed
    if (!currentUser?.id && articles.length === 0 && smartLinkLocalityRef.current) {
      const { localityIds, lat, lng } = smartLinkLocalityRef.current;
      console.log('🔗 Guest back from smart link - loading article locality feed');

      // Returning to the locality feed
      stampContext('locality');
      const myContext = 'locality';

      try {
        let feedBoundaryIds = null;
        let locationName = null;

        if (localityIds && localityIds.length > 0) {
          // Article has locality_ids — use them directly
          feedBoundaryIds = localityIds;
          console.log('🔗 Using article locality_ids:', localityIds);
        } else if (lat && lng) {
          // Article has no locality_ids but has coordinates — resolve to boundary
          console.log('🔗 Article has no locality_ids, resolving coordinates:', lat, lng);
          const { data: boundaryId, error: resolveError } = await supabaseFetch.rpc('resolve_point_to_boundary', {
            p_lat: lat,
            p_lng: lng
          });
          if (!resolveError && boundaryId) {
            feedBoundaryIds = [boundaryId];
            console.log('🔗 Resolved to boundary ID:', boundaryId);
          }
        }

        if (feedBoundaryIds && feedBoundaryIds.length > 0) {
          // Look up the locality name for the header
          const { data: boundary } = await supabaseFetch.query('admin_boundaries', {
            select: 'name',
            eq: { id: feedBoundaryIds[0] },
            single: true
          });
          if (boundary?.name) {
            locationName = boundary.name;
            setUserHomeLocationName(locationName);
            console.log('🔗 Set location name:', locationName);
          }

          // An active visit takes precedence, so the Back destination matches what a normal
          // front-page load would show
          feedBoundaryIds = resolveScopeBoundaryIds({
            fallbackBoundaryIds: feedBoundaryIds,
            visitBoundaryIds: visitArea?.boundaryIds
          });

          // Load the FULL locality feed — news, chats and events — matching loadInitialData
          const [articlesData, chatsData, eventsData] = await Promise.all([
            fetchLocalityNews(null, { boundaryIds: feedBoundaryIds }),
            chatService.getLocalityChats(feedBoundaryIds),
            fetchLocalityEvents(null, { boundaryIds: feedBoundaryIds })
          ]);
          if (activeContextRef.current !== myContext) return; // stale — user navigated away
          setArticlesWithMeta(articlesData);
          setChats(chatsData);
          setEventsWithMeta(eventsData);
          guestArticlesLoadedRef.current = true;
          console.log('🔗 Loaded', articlesData.length, 'articles,', chatsData.length, 'chats,', eventsData.length, 'events for article locality');
        }
      } catch (error) {
        console.error('❌ Error loading article locality feed:', error);
      } finally {
        // One-time use — always clear, even on the stale-guard early return or error
        smartLinkLocalityRef.current = null;
      }
    } else if (
      currentUser?.id &&
      currentContext === 'locality' &&
      articles.length === 0 && chats.length === 0 && events.length === 0
    ) {
      // Logged-in user who arrived via a shared link: the locality feed was never
      // loaded, and the guest branch above doesn't run for logged-in users.
      console.log('🔗 Logged-in back from smart link - reloading home locality feed');
      smartLinkLocalityRef.current = null; // consumed — logged-in users get their own home area
      await handleLoadLocalityArticles();
    }

    // Track navigation breadcrumb
    window.dispatchEvent(new CustomEvent('ournet:navigate', {
      detail: { view: 'feed' }
    }));
  };

  // Navigate to Group from Article/Event/Chat
  const handleViewGroup = async (communityId, communityName) => {
    leaveDemoUnlessDemoId(communityId); // a live group opened from a demo page ends demo mode
    console.log('👥 Viewing group:', communityName, communityId);

    try {
      // Get full community details
      const community = await communityService.getCommunityById(communityId);

      if (community) {
        // Check if user is already a member (skip landing page for members)
        let isMember = false;
        if (isLoggedIn && currentUser?.id) {
          const isOwner = community.owner_id === currentUser.id;
          if (isOwner) {
            isMember = true;
          } else {
            const membership = await groupService.checkMembership(currentUser.id, communityId);
            isMember = membership.isMember;
          }
        }

        // Non-members: check for a published landing page
        if (!isMember) {
          try {
            const { data: landingPage } = await supabaseFetch.query('community_landing', {
              select: 'id',
              eq: { community_id: communityId, is_published: true },
              single: true
            });

            if (landingPage) {
              // Landing page exists - show it instead of group feed
              console.log('📄 Showing landing page for group:', community.name);
              setPreviousContext(currentContext);
              setLandingPageGroup(community);
              setCurrentView('group-landing-view');
              setSelectedArticle(null);
              setSelectedChat(null);
              setSelectedEvent(null);

              // Track navigation breadcrumb
              window.dispatchEvent(new CustomEvent('ournet:navigate', {
                detail: { view: 'group-landing-view', group: community }
              }));
              return;
            }
          } catch (err) {
            // No landing page found or error - fall through to default behavior
          }
        }

        // Member, or no landing page - go directly to group feed
        setCurrentContext('groups');
        setSelectedGroup(community);
        setCurrentMode('news');

        // Load content for this group
        await handleLoadArticlesForGroup(communityId);
        await handleLoadChatsForGroup(communityId);
        await handleLoadEventsForGroup(communityId);

        // Navigate to feed
        setCurrentView('feed');
        setSelectedArticle(null);
        setSelectedChat(null);
        setSelectedEvent(null);

        // Track navigation breadcrumb
        window.dispatchEvent(new CustomEvent('ournet:navigate', {
          detail: { view: 'feed', group: community }
        }));

        console.log('✅ Switched to group view:', community.name);
      } else {
        console.error('❌ Community not found:', communityId);
      }
    } catch (error) {
      console.error('❌ Error viewing group:', error);
    }
  };

  // Chat Response Posting
  const handlePostResponse = async (responseData) => {
    console.log('💬 Posting chat response');

    try {
      // Use the selected chat's community_id, not the current sidebar community
      const responseWithCommunity = {
        ...responseData,
        community_id: selectedChat?.community_id || currentCommunity?.id || null,
        author_id: currentUser?.id
      };

      await chatService.createResponse(responseWithCommunity);

      // Reload chats for the chat's community
      const chatCommunityId = selectedChat?.community_id || currentCommunity?.id || null;
      // Stamp matches the content being loaded: a group chat's community, else locality.
      const myContext = chatCommunityId ? 'group:' + chatCommunityId : 'locality';
      const updatedChats = await chatService.getOriginalChats(chatCommunityId);
      if (activeContextRef.current !== myContext) return; // stale — user navigated away
      setChats(updatedChats);

      // Reload responses for the current chat detail view
      if (selectedChat) {
        const updatedResponses = await chatService.getThreadChats(selectedChat.id);
        setSelectedChatResponses(updatedResponses || []);
      }

      console.log('✅ Response posted successfully');

    } catch (error) {
      console.error('❌ Error posting response:', error?.message || error);
      // Handed back to ChatDetail, which is inside the translation provider and says what went
      // wrong in the member's language — a rate limit names the action and the wait (addendum 3
      // item 8). Replaces the two English sentences this pre-provider modal used to show.
      throw error;
    }
  };

  // User Profile Navigation
  const handleShowMyProfile = (userId = null) => {
    if (!isLoggedIn) {
      setPendingNavigation({ action: 'profile' });
      setShowLoginModal(true);
      return;
    }
    setPreviousContext(currentContext);
    // Only set to currentUser if no specific userId was requested
    if (userId) {
      setSelectedProfileUserId(userId);
    } else {
      setSelectedProfileUserId(currentUser?.id || null);
    }
    setCurrentView('profile');

    // NOTE: Do NOT dispatch 'ournet:navigate' event here - this function is already
    // called BY the navigation event handler (handleCustomNavigate), so dispatching
    // another event here creates an infinite loop.
  };

  const handleShowMyArticles = () => {
    if (!isLoggedIn) {
      setPendingNavigation({ action: 'articles' });
      setShowLoginModal(true);
      return;
    }
    setPreviousContext(currentContext);
    setSelectedProfileUserId(currentUser?.id || null);
    setCurrentView('articles');

    // NOTE: Do NOT dispatch 'ournet:navigate' event here - creates infinite loop
  };

  const handleShowMyTags = () => {
    if (!isLoggedIn) {
      setPendingNavigation({ action: 'tags' });
      setShowLoginModal(true);
      return;
    }
    setPreviousContext(currentContext);
    setSelectedProfileUserId(currentUser?.id || null);
    setCurrentView('tags');

    // NOTE: Do NOT dispatch 'ournet:navigate' event here - creates infinite loop
  };

  const handleShowMyLocations = (setHomeSeed = null) => {
    if (!isLoggedIn) {
      setPendingNavigation({ action: 'locations' });
      setShowLoginModal(true);
      return;
    }
    setPreviousContext(currentContext);
    setPreviousView(currentView); // Save current view to return to
    setSelectedProfileUserId(currentUser?.id || null);
    // Unconditional overwrite (not "only if truthy") — a plain "Manage Locations" open must not
    // inherit a stale seed left over from state, even though every other locations-view entry
    // point already clears it on the way out (onBack, App.js:3392-3400).
    setMyLocationsSetHomeSeed(setHomeSeed);
    setCurrentView('locations');

    // NOTE: Do NOT dispatch 'ournet:navigate' event here - creates infinite loop
  };

  const handleShowStewardWorkspace = () => {
    if (!isLoggedIn) {
      setPendingNavigation({ action: 'steward' });
      setShowLoginModal(true);
      return;
    }
    setPreviousContext(currentContext);
    setPreviousView(currentViewRef.current);
    setCurrentView('steward');
  };

  const handleShowEventsImporter = () => {
    if (!isLoggedIn) {
      setPendingNavigation({ action: 'events-importer' });
      setShowLoginModal(true);
      return;
    }
    setPreviousContext(currentContext);
    setPreviousView(currentViewRef.current);
    setCurrentView('events-importer');
  };

  const handleShowEventsPending = () => {
    if (!isLoggedIn) {
      setPendingNavigation({ action: 'events-pending' });
      setShowLoginModal(true);
      return;
    }
    setPreviousContext(currentContext);
    setPreviousView(currentViewRef.current);
    setCurrentView('events-pending');
  };

  const handleShowNewArticle = (mode = 'add', article = null, groupId = null) => {
    setPreviousContext(currentContext);
    setPreviousView(currentView); // Save current view to return to (matches handleShowNewChat pattern)
    setSelectedProfileUserId(currentUser?.id || null);
    setArticleMode(mode);
    setArticleToEdit(article);
    setPreselectedGroupId(groupId);
    setCurrentView('new-article');

    // NOTE: Do NOT dispatch 'ournet:navigate' event here - creates infinite loop
  };

  const handleShowNewChat = (mode = 'add', chat = null, groupId = null) => {
    setPreviousContext(currentContext);
    setPreviousView(currentView); // Save current view (e.g., 'chat' or 'feed')
    setSelectedProfileUserId(currentUser?.id || null);
    setChatMode(mode);
    setChatToEdit(chat);
    setPreselectedChatGroupId(groupId);
    setCurrentView('new-chat');

    // NOTE: Do NOT dispatch 'ournet:navigate' event here - creates infinite loop
  };

  // Handler for "New Chat" button in ChatGrid
  const handleNewChat = () => {
    if (selectedGroup?.id) {
      handleShowNewChat('add', null, selectedGroup.id);
    } else {
      // If no group selected, show an alert or handle appropriately
      showAppModal('Please select a group first to create a new chat.');
    }
  };

  const handleShowNewEvent = (mode = 'add', event = null, groupId = null) => {
    setPreviousContext(currentContext);
    setSelectedProfileUserId(currentUser?.id || null);
    setEventMode(mode);
    setEventToEdit(event);
    setPreselectedEventGroupId(groupId);
    setCurrentView('new-event');

    // NOTE: Do NOT dispatch 'ournet:navigate' event here - creates infinite loop
  };

  const handleShowNewGroup = (mode = 'add', group = null, parentGroup = null, prefill = null) => {
    setPreviousContext(currentContext);
    // Creating/editing a group is unambiguously a Groups action — mauve everywhere, regardless
    // of which page it was triggered from (same reasoning as handleGroupSelect). Callers already
    // inside 'groups' context (GroupsGrid, GroupHomeColumn sub-group add) get a no-op here;
    // callers reached from Locality (GroupsColumn's "+") get the fix. handleBackFromMy already
    // restores previousContext on cancel/back, captured just above.
    setCurrentContext('groups');
    setSelectedProfileUserId(currentUser?.id || null);
    setGroupMode(mode);
    setGroupToEdit(group);
    setParentGroupForNew(parentGroup);
    setGroupPrefill(prefill);
    setCurrentView('new-group');

    // NOTE: Do NOT dispatch 'ournet:navigate' event here - creates infinite loop
  };

  const handleShowAdminGroup = (group = null, isPostCreate = false) => {
    setPreviousContext(currentContext);
    setSelectedProfileUserId(currentUser?.id || null);
    setGroupToEdit(group);
    setPostCreateGroup(isPostCreate);
    setCurrentView('admin-group');

    // NOTE: Do NOT dispatch 'ournet:navigate' event here - creates infinite loop
  };

  // Show Landing Page Editor
  const handleShowLandingPage = (group = null, isPostCreate = false) => {
    setPreviousContext(currentContext);
    setSelectedProfileUserId(currentUser?.id || null);
    setAddEditLandingPageGroup(group);
    setPostCreateLandingPage(isPostCreate);
    setCurrentView('group-landing');

    // NOTE: Do NOT dispatch 'ournet:navigate' event here - creates infinite loop
  };

  // Show Landing Page Read-Only View
  const handleShowLandingPageView = (group = null, fromGate = false) => {
    leaveDemoUnlessDemoId(group?.id); // a live group's landing page opened from a demo page ends demo mode
    setPreviousContext(currentContext);
    // A group's landing page is unambiguously Groups-themed, same reasoning as
    // handleGroupSelect/handleShowNewGroup — reachable from Locality (GroupsColumn's
    // visitGroup, useGroups.js) where currentContext would otherwise stay 'locality'.
    // handleBackFromMy/handleGroupGateBack (this view's onBack) already restore
    // previousContext, captured just above.
    setCurrentContext('groups');
    setLandingPageGroup(group);
    setLandingFromGate(!!fromGate); // Tier 2: gate redirect → Back goes to the previous group's Home
    setCurrentView('group-landing-view');

    // NOTE: Do NOT dispatch 'ournet:navigate' event here - creates infinite loop
  };

  // Tier 2 — Back from the §4 gate (GroupPrivateGate, or a gate-redirected landing page).
  // Returns to the previous group's Home tab; if there's no previous group, to group discovery.
  // It MUST move selectedGroup off the gated group — otherwise the gate re-fires immediately
  // and the landing page loops straight back.
  const handleGroupGateBack = () => {
    setLandingFromGate(false);
    setLandingPageGroup(null);
    setPreviousContext(null);
    setCurrentContext('groups');
    setCurrentView('feed');
    setFrontPageActive(true);
    if (previousGroup) {
      setSelectedGroup(previousGroup);
      setPreviousGroup(null);
      setCurrentMode('home'); // land on the previous group's Home/Info tab (mobile)
    } else {
      setSelectedGroup(null); // no previous group → group discovery (GroupsGrid)
    }
  };

  // Load chats for a specific group/community
  const handleLoadChatsForGroup = async (groupId) => {
    if (!groupId) {
      console.warn('⚠️ No group ID provided for loading chats');
      return;
    }

    stampContext('group:' + groupId);
    const myContext = 'group:' + groupId;

    try {
      console.log('📡 Loading chats for group:', groupId);
      const groupChats = await chatService.getOriginalChats(groupId);
      if (activeContextRef.current !== myContext) return; // stale — user navigated away
      setChats(groupChats);
      console.log('✅ Loaded', groupChats.length, 'chats for group:', groupId);
    } catch (error) {
      console.error('❌ Error loading chats for group:', error);
      if (activeContextRef.current !== myContext) return; // stale — don't wipe current content
      setChats([]); // Clear chats on error
    }
  };

  const handleLoadArticlesForGroup = async (groupId) => {
    if (!groupId) {
      console.warn('⚠️ No group ID provided for loading articles');
      return;
    }

    stampContext('group:' + groupId);
    const myContext = 'group:' + groupId;

    try {
      console.log('📡 Loading articles for group:', groupId);
      const groupArticles = await articleService.getGroupArticles(groupId);
      if (activeContextRef.current !== myContext) return; // stale — user navigated away
      setArticlesWithMeta(groupArticles);
      console.log('✅ Loaded', groupArticles.length, 'articles for group:', groupId);
    } catch (error) {
      console.error('❌ Error loading articles for group:', error);
      if (activeContextRef.current !== myContext) return; // stale — don't wipe current content
      setArticlesWithMeta([]); // Clear articles on error
    }
  };

  // Load events for a specific group/community
  const handleLoadEventsForGroup = async (groupId) => {
    if (!groupId) {
      console.warn('⚠️ No group ID provided for loading events');
      return;
    }

    stampContext('group:' + groupId);
    const myContext = 'group:' + groupId;

    try {
      console.log('📡 Loading events for group:', groupId);
      const groupEvents = await eventService.getEvents(groupId);
      if (activeContextRef.current !== myContext) return; // stale — user navigated away
      setEventsWithMeta(groupEvents);
      console.log('✅ Loaded', groupEvents.length, 'events for group:', groupId);
    } catch (error) {
      console.error('❌ Error loading events for group:', error);
      if (activeContextRef.current !== myContext) return; // stale — don't wipe current content
      setEventsWithMeta([]); // Clear events on error
    }
  };

  // ── Guest location change: picker selected a new boundary ──
  const handleGuestLocationChange = async (selection) => {
    // selection = { boundaryId, name, countryCode, lat, lon }
    if (!selection?.boundaryId) return;

    // Guest changing location stays in the locality feed
    stampContext('locality');
    const myContext = 'locality';

    // 1. Update display state
    setUserHomeLocationName(selection.name);
    if (selection.lat && selection.lon) {
      setUserHomeLocationCoords({ lat: selection.lat, lon: selection.lon });
    }

    // 2. Update cached boundary for registration inheritance
    setDetectedBoundaryId(selection.boundaryId);
    detectedBoundaryIdRef.current = selection.boundaryId;

    // 3. Update localStorage cache so refresh keeps the choice
    try {
      const cached = localStorage.getItem('ournet_ip_location');
      if (cached) {
        const cacheObj = JSON.parse(cached);
        cacheObj.data.boundaryId = selection.boundaryId;
        cacheObj.data.city = selection.name;
        if (selection.lat) cacheObj.data.latitude = selection.lat;
        if (selection.lon) cacheObj.data.longitude = selection.lon;
        if (selection.countryCode) cacheObj.data.countryCode = selection.countryCode;
        localStorage.setItem('ournet_ip_location', JSON.stringify(cacheObj));
      }
    } catch (e) {
      // Cache update failed — non-fatal
    }

    // 4. Reload feed with the picked boundary (same RPC path logged-in users take)
    try {
      const boundaryIds = [selection.boundaryId];
      // Routed through eventService.getLocalityEvents (docs/briefs/2026-08-13-events-ordering-
      // by-start-time.md + addendum-1) rather than a hand-rolled RPC call, for two reasons: it
      // already requests both Future/Past windows in parallel (the fix this build exists for),
      // and it keeps this fetch in /src/data/ rather than duplicating RPC-calling logic here,
      // matching articleService.getLocalityNews on the line above it (FILE_STRUCTURE.md § Data
      // Layer). event_details was already fetched separately downstream either way — every
      // consumer of the `events` state re-fetches and overwrites those fields itself.
      const [articlesData, eventsData] = await Promise.all([
        fetchLocalityNews(null, { boundaryIds }),
        fetchLocalityEvents(null, { boundaryIds })
      ]);

      if (activeContextRef.current !== myContext) return; // stale — user navigated away
      setArticlesWithMeta(articlesData);
      setEventsWithMeta(eventsData);
      console.log('✅ Guest feed reloaded for boundary:', selection.name, '— Articles:', articlesData.length, 'Events:', eventsData.length);
    } catch (err) {
      console.error('❌ Guest feed reload failed:', err);
    }
  };

  // ── Enter a temporary visit (logged-in only): picker selected a boundary to browse ──
  // Session-only — never touches user_areas. Reuses handleLoadLocalityArticles for the actual
  // reload: once visitArea is set, that function's own resolveScopeBoundaryIds call (below) picks
  // the visited boundary over home automatically, so no separate fetch is duplicated here.
  const handleEnterVisit = (selection) => {
    if (!selection?.boundaryId) return;
    // lat/lon carried for the weather widget + left-column name (§2, 2026-09-02 fix) — same
    // selection shape and same purpose handleGuestLocationChange already uses them for.
    const visitState = {
      boundaryIds: [selection.boundaryId],
      displayName: selection.name,
      lat: selection.lat ?? null,
      lon: selection.lon ?? null
    };
    setVisitArea(visitState);
    visitAreaRef.current = visitState;
    handleLoadLocalityArticles();
  };

  // ── Clear an active visit, no reload of its own (addendum-4 §2) ──
  // The separate "Return home" control is retired — the only way to end a visit now is
  // selecting one of the user's own 10 area slots in the dial, which calls this synchronously
  // BEFORE its own ournet:area-changed dispatch (HeaderButtonsFeed's handleSelectArea), so
  // visitAreaRef.current is already null by the time handleAreaChanged's resolver reads it below
  // and the reload naturally uses the newly-selected area's own boundary ids, not the visit.
  const handleClearVisit = () => {
    setVisitArea(null);
    visitAreaRef.current = null;
  };


  const handleLoadLocalityArticles = async () => {
    // Guest users: locality articles are IP-loaded once on initial load and must PERSIST across
    // benign context switches (03a98d43) — do not clear them. EXCEPTION: if the currently-loaded
    // content belongs to a GROUP (the guest is returning group→locality), the shared array holds
    // the group's articles; reload the guest locality feed from the IP/picked boundary so locality
    // shows its own content again. Without this, the group article leaks into the guest locality
    // feed and locality's own articles stay missing. This is context-scoped — it does NOT re-enable
    // guest clearing on a locality→locality / benign switch.
    if (!currentUser?.id) {
      if (activeContextRef.current.startsWith('group:')) {
        const boundaryId = detectedBoundaryIdRef.current;
        if (boundaryId) {
          stampContext('locality');
          const myContext = 'locality';
          try {
            // Reads visitAreaRef, not visitArea state — see the logged-in branch's identical fix
            // below for why (2026-09-02 stale-closure fix).
            const boundaryIds = resolveScopeBoundaryIds({
              fallbackBoundaryIds: [boundaryId],
              visitBoundaryIds: visitAreaRef.current?.boundaryIds
            });
            const coords = userHomeLocationCoords;
            const [guestArticles, guestChats, guestEvents] = await Promise.all([
              fetchLocalityNews(null, { boundaryIds }),
              chatService.getLocalityChats(boundaryIds),
              fetchLocalityEvents(null, { boundaryIds, guestCoords: coords ? { lat: coords.lat, lng: coords.lon } : undefined })
            ]);
            if (activeContextRef.current !== myContext) return; // stale — user navigated away
            setArticlesWithMeta(guestArticles);
            setChats(guestChats);
            setEventsWithMeta(guestEvents);
            guestArticlesLoadedRef.current = true;
            console.log('✅ Guest locality feed reloaded after leaving a group — Articles:', guestArticles.length);
          } catch (err) {
            console.error('❌ Guest locality reload (from group) failed:', err);
          }
        }
      }
      return; // otherwise preserve existing guest locality articles (original behaviour)
    }

    stampContext('locality');
    const myContext = 'locality';

    try {
      console.log('📡 Loading locality articles...');

      // Get user's home area for geocentric content filtering (NEW - MVP Geocentric)
      let userHomeAreaId = null;
      const { data: homeArea } = await supabaseFetch.query('user_areas', {
        select: 'id,area_code,display_name,boundary_ids,primary_boundary_id',
        eq: { user_id: currentUser.id, is_home: true },
        single: true
      });

      let loadBoundaryIds = null;
      if (homeArea) {
        userHomeAreaId = homeArea.id;
        loadBoundaryIds = homeArea.boundary_ids || [];

        if (loadBoundaryIds.length > 0) {
          // New system: derive display name and coords from boundary centroid
          const { data: centroid } = await supabaseFetch.rpc('get_boundary_centroid', {
            p_boundary_ids: headlineBoundaryIds(homeArea)
          });
          if (centroid?.[0]) {
            setUserHomeLocationName(centroid[0].name);
            setUserHomeLocationCoords({ lat: centroid[0].lat, lon: centroid[0].lon });
            console.log('✅ Home area (boundary):', centroid[0].name, homeArea.id);
          } else {
            setUserHomeLocationName(homeArea.display_name);
          }
        } else {
          // Legacy system: derive coords from user_area_localities → localities
          const { data: areaLocality } = await supabaseFetch.query('user_area_localities', {
            select: 'locality_id',
            eq: { area_id: homeArea.id, display_order: 0 },
            single: true
          });

          if (areaLocality?.locality_id) {
            const { data: locality } = await supabaseFetch.query('localities', {
              select: 'name,latitude,longitude',
              eq: { id: areaLocality.locality_id },
              single: true
            });
            if (locality?.name) {
              setUserHomeLocationName(locality.name);
              if (locality.latitude && locality.longitude) {
                setUserHomeLocationCoords({ lat: locality.latitude, lon: locality.longitude });
              }
              console.log('✅ Home area (locality):', locality.name, homeArea.id);
            } else {
              setUserHomeLocationName(homeArea.display_name);
            }
          } else {
            setUserHomeLocationName(homeArea.display_name);
          }
        }
      } else {
        console.warn('⚠️ No home area found for user');
      }

      // Load all public content from user's home area (articles, chats, events)
      // An active visit (Change 3) takes precedence over home — this is the site handleEnterVisit reuses for its reload.
      // Reads visitAreaRef, NOT visitArea state: handleEnterVisit calls this function
      // synchronously, in the same render, immediately after setVisitArea — the state setter
      // hasn't committed yet at that point, so visitArea here would still be the pre-click
      // value (stale-closure bug diagnosed 2026-09-02, RESULTS 8fc2e796). visitAreaRef.current
      // is updated synchronously at the same call site as setVisitArea, so it's always correct
      // by the time this line runs, regardless of which caller triggered it.
      const feedBoundaryIds = resolveScopeBoundaryIds({
        fallbackBoundaryIds: loadBoundaryIds,
        visitBoundaryIds: visitAreaRef.current?.boundaryIds
      });

      const [localityArticles, localityChats, localityEvents] = await Promise.all([
        fetchLocalityNews(userHomeAreaId, { boundaryIds: feedBoundaryIds }),
        chatService.getLocalityChats(feedBoundaryIds),
        fetchLocalityEvents(userHomeAreaId, { boundaryIds: feedBoundaryIds })
      ]);
      if (activeContextRef.current !== myContext) return; // stale — user navigated away
      setArticlesWithMeta(localityArticles);
      setChats(localityChats);
      setEventsWithMeta(localityEvents);
      console.log('✅ Loaded locality content - Articles:', localityArticles.length, 'Chats:', localityChats.length, 'Events:', localityEvents.length);
    } catch (error) {
      console.error('❌ Error loading locality content:', error);
      if (activeContextRef.current !== myContext) return; // stale — don't wipe current content
      setArticlesWithMeta([]);
      setChats([]);
      setEventsWithMeta([]);
    }
  };

  const handleBackFromMy = () => {
    // Capture current state before clearing
    const wasInLocations = currentView === 'locations';
    const wasLocalityContext = previousContext === 'locality';
    const wasNewChat = currentView === 'new-chat';
    const chatGroupToReload = wasNewChat ? preselectedChatGroupId : null;

    // Navigate immediately (don't block on data loading)
    if (previousContext) {
      setCurrentContext(previousContext);
    }

    if (previousView) {
      setCurrentView(previousView);
      setPreviousView(null);
    } else {
      setCurrentView('feed');
    }

    setSelectedProfileUserId(null);
    setPreviousContext(null);
    setLandingFromGate(false); // Tier 2: clear the gate-back flag on any normal back

    // Reload data in background after navigation
    if (wasNewChat && wasLocalityContext && !chatGroupToReload) {
      // Locality chat created — reload locality chats
      const reloadLocalityChats = async () => {
        const myContext = 'locality';
        try {
          const bids = selectedArea?.boundary_ids || [];
          const feedBids = resolveScopeBoundaryIds({
            fallbackBoundaryIds: bids,
            visitBoundaryIds: visitArea?.boundaryIds
          });
          if (feedBids.length > 0) {
            const localityChats = await chatService.getLocalityChats(feedBids);
            if (activeContextRef.current !== myContext) return; // stale — user navigated away
            setChats(localityChats);
          }
        } catch (error) {
          console.error('❌ Error reloading locality chats:', error);
        }
      };
      reloadLocalityChats();
    } else if (chatGroupToReload) {
      handleLoadChatsForGroup(chatGroupToReload).catch(error => {
        console.error('❌ Error reloading chats:', error);
      });
    }

    if (wasInLocations && wasLocalityContext) {
      handleLoadLocalityArticles().catch(error => {
        console.error('❌ Error reloading locality articles:', error);
      });
    }
  };

  // ============================================================================
  // AUTH HANDLERS
  // ============================================================================

  const handleLoginSuccess = (user) => {
    console.log('✅ Login successful:', user.email);
    // Before pendingNavigation below, which is the navigation this sign-in was for.
    resetForIdentityChange('signed-in', user.id);
    // is_sys_admin isn't on this login-response shape — the SIGNED_IN auth listener above fires
    // within moments and replaces this with a full profile fetch that does carry it.
    setCurrentUser({
      id: user.id,
      display_name: user.display_name,
      email: user.email,
      avatar_url: user.avatar_url || null,
      is_sys_admin: false
    });
    setIsLoggedIn(true);
    setShowLoginModal(false);

    // Defensive clear: a fresh login should never inherit a stale visit from a prior session in
    // the same tab (addendum-1 §C). handleLogout already clears this on the normal path.
    setVisitArea(null);
    visitAreaRef.current = null;

    // Clear guest articles flag - authenticated users need content cleared on context switch
    guestArticlesLoadedRef.current = false;

    // Refresh user-specific data (areas/localities in header) with small delay
    // Include userId so HeaderButtonsFeed can use it immediately (React state may not have propagated yet)
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('ournet:refresh-locations', {
        detail: { userId: user.id }
      }));
    }, 100);

    // Execute pending navigation if any
    if (pendingNavigation) {
      const { action } = pendingNavigation;
      setPendingNavigation(null);

      switch(action) {
        case 'profile':
          handleShowMyProfile();
          break;
        case 'articles':
          handleShowMyArticles();
          break;
        case 'tags':
          handleShowMyTags();
          break;
        case 'locations':
          handleShowMyLocations();
          break;
        case 'events-importer':
          handleShowEventsImporter();
          break;
        case 'events-pending':
          handleShowEventsPending();
          break;
        default:
          console.warn('Unknown pending navigation action:', action);
      }
    }
  };

  const handleCloseLoginModal = () => {
    setShowLoginModal(false);
    setPendingNavigation(null);
    setAuthRequiredMessage(''); // clear gate copy so the next open isn't stale
    setLoginModalMode('login'); // reset so the next open defaults to login unless a caller asks otherwise
  };

  const handleLogout = async () => {
    console.log('🔓 User logging out...');

    try {
      // Sign out from Supabase Auth
      await supabase.auth.signOut();
    } catch (err) {
      console.error('Error signing out:', err);
    }

    // Clean start (Roadmap #100): selected area, visit, group, content and the guest reload. A
    // no-op when the SIGNED_OUT listener has already run it during signOut() above; it runs here
    // when signOut() failed and no event came.
    resetForIdentityChange('signed-out');

    // Clear local state
    setIsLoggedIn(false);
    setCurrentUser(null);
    setShowLoginModal(false);
    setPendingNavigation(null);

    // Clear any old credential storage
    localStorage.removeItem('ournet_saved_email');
    localStorage.removeItem('ournet_saved_password');
  };

  const handleShowLoginModal = () => {
    console.log('📋 Showing login modal');
    setShowLoginModal(true);
  };

  // LocationSetupModal handlers removed — replaced by homeAreaSelfHealer

  // ============================================================================
  // CUSTOM NAVIGATION EVENTS
  // ============================================================================
  
  // Listen for custom navigation events from UI components
  useEffect(() => {
    const handleCustomNavigate = async (e) => {
      const { view, userId, mode, article, scrollToTop, groupId, group, parentGroup, postCreate, fromGate, prefill, seedBoundaryId } = e?.detail || {};

      console.log('🔔 Custom navigation event:', view, groupId ? `(groupId: ${groupId})` : '');

      switch(view) {
        case 'profile':
          handleShowMyProfile(userId || currentUser?.id || null);
          break;

        case 'articles':
          setSelectedProfileUserId(userId || currentUser?.id || null);
          handleShowMyArticles();
          if (scrollToTop) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
          break;

        case 'new-article':
          handleShowNewArticle(mode || 'add', article || null, groupId || null);
          break;

        case 'walking-composer':
          // Auth guard — walking composer requires a logged-in user
          if (!isLoggedIn || !currentUser?.id) {
            window.dispatchEvent(new CustomEvent('ournet:showAuthRequired', {
              detail: { message: 'Please log in to create an article.' }
            }));
            break;
          }
          setPreviousContext(currentContext);
          setSelectedProfileUserId(currentUser?.id || null);
          setPreselectedGroupId(groupId || null);
          setCurrentView('walking-composer');
          break;

        case 'new-chat':
          handleShowNewChat(mode || 'add', null, groupId || null);
          break;

        case 'new-event':
          handleShowNewEvent(mode || 'add', article || null, groupId || null);
          break;

        case 'new-group':
          handleShowNewGroup(mode || 'add', group || null, parentGroup || null, prefill || null);
          break;

        case 'group':
          // Reached from PresencePanel's "View group" link (claimed directory listings, Directory
          // rail) — had NO case here at all (fell to the unknown-view warning below and did
          // nothing). Mirrors the #/group/{id} hash handler's already-correct logic.
          leaveDemoUnlessDemoId(groupId); // a live group opened from a demo page ends demo mode
          try {
            const community = await communityService.getCommunityById(groupId);
            if (community) {
              setPreviousContext(currentContext);
              setCurrentContext('groups');
              setSelectedGroup(community);
              setCurrentMode('news');
              setCurrentView('feed');
              clearInvitationHash();
            }
          } catch (error) {
            console.error('❌ Error loading group:', error);
          }
          break;

        case 'admin-group':
          handleShowAdminGroup(group || null, postCreate || false);
          break;

        case 'group-landing':
          handleShowLandingPage(group || null, postCreate || false);
          break;

        case 'group-landing-view':
          handleShowLandingPageView(group || null, fromGate || false);
          break;

        case 'tags':
          handleShowMyTags();
          break;

        case 'locations':
          handleShowMyLocations(seedBoundaryId ? { boundaryId: seedBoundaryId } : null);
          break;

        case 'steward':
          handleShowStewardWorkspace();
          break;

        case 'events-importer':
          handleShowEventsImporter();
          break;

        case 'events-pending':
          handleShowEventsPending();
          break;

        case 'images':
          setSelectedProfileUserId(userId || currentUser?.id || null);
          setPreviousContext(currentContext);
          setCurrentView('images');
          break;

        case 'my-groups':
          // Navigate to Groups context with "Groups I Own" mode pre-selected
          setPreviousContext(currentContext);
          setCurrentContext('groups');
          setSelectedGroup(null);
          setGroupsInitialMode('owned');
          setCurrentView('feed');
          break;

        case 'admin-analytics':
          // Navigate to admin analytics dashboard. Remember where it was opened from (the Admin
          // Panel's Statistics tab), so its Back returns there rather than to the feed — same
          // currentViewRef idiom as handleShowStewardWorkspace and its siblings.
          console.log('📊 Opening admin analytics via custom event');
          setPreviousView(currentViewRef.current);
          setCurrentView('admin-analytics');
          break;

        case 'admin':
          // Navigate to admin panel
          console.log('🛡️ Opening admin panel via custom event');
          setCurrentView('admin');
          break;

        case 'feed':
        case 'home':
        case '/':
        case '':
          setCurrentView('feed');
          clearInvitationHash();
          break;

        default:
          console.warn('⚠️ Unknown navigation view:', view);
      }
    };

    window.addEventListener('ournet:navigate', handleCustomNavigate);
    return () => window.removeEventListener('ournet:navigate', handleCustomNavigate);
  }, [currentUser, currentContext]);

  // Listen for navigate-to-admin event (from Admin button in HeaderButtonsFeed)
  useEffect(() => {
    const handleNavigateToAdmin = () => {
      console.log('🛡️ Navigate-to-admin event received');
      setCurrentView('admin');
    };

    window.addEventListener('navigate-to-admin', handleNavigateToAdmin);
    return () => window.removeEventListener('navigate-to-admin', handleNavigateToAdmin);
  }, []);

  // Listen for article changes and reload articles data
  useEffect(() => {
    const handleArticlesChanged = async () => {
      console.log('🔄 Articles changed - reloading articles data...');
      try {
        if (!currentUser?.id) return;

        // In groups context, reload the group's articles instead of locality articles
        if (currentContext === 'groups' && selectedGroup?.id) {
          console.log('🔄 Reloading group articles for:', selectedGroup.name);
          const groupContext = 'group:' + selectedGroup.id;
          const [groupArticles, groupEvents] = await Promise.all([
            articleService.getGroupArticles(selectedGroup.id),
            eventService.getEvents(selectedGroup.id)
          ]);
          if (activeContextRef.current !== groupContext) return; // stale — user navigated away
          setArticlesWithMeta(groupArticles);
          setEventsWithMeta(groupEvents);
          console.log('✅ Group articles reloaded:', groupArticles.length, '— events:', groupEvents.length);
          return;
        }

        // Locality branch — content being loaded is the locality feed
        const myContext = 'locality';

        // Locality context — reload locality articles
        const { data: homeArea } = await supabaseFetch.query('user_areas', {
          select: 'id,area_code,display_name,boundary_ids,primary_boundary_id',
          eq: { user_id: currentUser.id, is_home: true },
          single: true
        });

        if (homeArea) {
          setUserHomeLocationName(homeArea.display_name);

          if (homeArea.boundary_ids && homeArea.boundary_ids.length > 0) {
            const { data: centroid } = await supabaseFetch.rpc('get_boundary_centroid', {
              p_boundary_ids: headlineBoundaryIds(homeArea)
            });

            if (centroid && centroid.length > 0) {
              setUserHomeLocationCoords({
                lat: centroid[0].lat,
                lon: centroid[0].lon
              });
            }

            // Reload articles + events using boundary system
            // An active visit takes precedence. Reads visitAreaRef, not visitArea state
            // — this listener is registered with a fixed dep array that doesn't include it.
            const reloadBoundaryIds = resolveScopeBoundaryIds({
              fallbackBoundaryIds: homeArea.boundary_ids,
              visitBoundaryIds: visitAreaRef.current?.boundaryIds
            });
            const [articlesData, eventsData] = await Promise.all([
              fetchLocalityNews(homeArea.id, { boundaryIds: reloadBoundaryIds }),
              fetchLocalityEvents(homeArea.id, { boundaryIds: reloadBoundaryIds })
            ]);
            if (activeContextRef.current !== myContext) return; // stale — user navigated away
            setArticlesWithMeta(articlesData);
            setEventsWithMeta(eventsData);
            console.log('✅ Articles reloaded:', articlesData.length, '— events:', eventsData.length);
          } else {
            console.warn('⚠️ Home area has no boundary_ids - user needs to set location');
            if (activeContextRef.current !== myContext) return; // stale — don't wipe current content
            setArticlesWithMeta([]);
            setEventsWithMeta([]);
          }
        } else {
          console.warn('⚠️ No home area found for user');
          if (activeContextRef.current !== myContext) return; // stale — don't wipe current content
          setArticlesWithMeta([]);
          setEventsWithMeta([]);
        }
      } catch (error) {
        console.error('❌ Error reloading articles:', error);
      }
    };

    window.addEventListener('ournet:articlesChanged', handleArticlesChanged);
    return () => window.removeEventListener('ournet:articlesChanged', handleArticlesChanged);
  }, [currentUser, currentContext, selectedGroup]); // eslint-disable-line react-hooks/exhaustive-deps -- fetchLocalityNews / fetchLocalityEvents read only refs and the services

  // Listen for content clear events (context switching and group selection)
  // CRITICAL: Prevents stale locality articles from appearing in groups context (and vice versa)
  useEffect(() => {
    const handleClearContent = () => {
      console.log('🧹 Clearing content arrays (context switch or group change)');
      // Preserve guest LOCALITY articles (5e811978 / 03a98d43) — IP-loaded, not easily reloadable.
      // But when the loaded content belongs to a GROUP, the guest is leaving the group, so clearing
      // is correct (handleLoadLocalityArticles then repopulates the guest locality feed). Only the
      // guest-on-locality case is preserved — NOT a blanket re-enable of guest clearing.
      if (guestArticlesLoadedRef.current && !activeContextRef.current.startsWith('group:')) return;
      setArticlesWithMeta([]);
      setChats([]);
      setEventsWithMeta([]);
    };

    window.addEventListener('ournet:clear-content', handleClearContent);
    return () => window.removeEventListener('ournet:clear-content', handleClearContent);
  }, []);

  // Listen for location changes (when user changes home area or reorders localities)
  useEffect(() => {
    const handleLocationsRefresh = async () => {
      console.log('🔄 Locations changed - updating home location and reloading articles...');
      try {
        // Skip if we're on a demo route (demo handler will load group content)
        const currentPath = window.location.pathname;
        const isDemoRoute = currentPath.match(/^\/demo\/([^/]+)$/); // exactly /demo/<slug>

        if (isDemoRoute) {
          console.log('🎭 Demo route active - skipping location refresh content reload');
          return;
        }

        if (currentUser?.id && currentContext === 'locality') {
          stampContext('locality');
          const myContext = 'locality';
          // Get user's updated home area
          let userHomeAreaId = null;
          const { data: homeArea } = await supabaseFetch.query('user_areas', {
            select: 'id,area_code,display_name,boundary_ids,primary_boundary_id',
            eq: { user_id: currentUser.id, is_home: true },
            single: true
          });

          if (homeArea) {
            userHomeAreaId = homeArea.id;
            const refreshBoundaryIds = homeArea.boundary_ids || [];

            // Home is the No.1/top locality — a home change (or any locations refresh while
            // viewing locality context) moves the active selectedArea with it, the same way
            // handleAreaChanged does for an explicit area switch, so Directory/Official Contacts
            // (which key off selectedArea via useDirectoryListings) follow the new home instead
            // of lagging one locality behind.
            setSelectedArea({
              id: homeArea.id,
              area_code: homeArea.area_code,
              display_name: homeArea.display_name,
              boundary_ids: refreshBoundaryIds,
              primary_boundary_id: homeArea.primary_boundary_id || null
            });

            if (refreshBoundaryIds.length > 0) {
              const { data: centroid } = await supabaseFetch.rpc('get_boundary_centroid', {
                p_boundary_ids: headlineBoundaryIds(homeArea)
              });
              if (centroid?.[0]) {
                setUserHomeLocationName(centroid[0].name);
                setUserHomeLocationCoords({ lat: centroid[0].lat, lon: centroid[0].lon });
                console.log('✅ Updated home location (boundary):', centroid[0].name);
              } else {
                setUserHomeLocationName(homeArea.display_name);
              }
            } else {
              const { data: areaLocality } = await supabaseFetch.query('user_area_localities', {
                select: 'locality_id',
                eq: { area_id: homeArea.id, display_order: 0 },
                single: true
              });

              if (areaLocality?.locality_id) {
                const { data: locality } = await supabaseFetch.query('localities', {
                  select: 'name,latitude,longitude',
                  eq: { id: areaLocality.locality_id },
                  single: true
                });
                if (locality?.name) {
                  setUserHomeLocationName(locality.name);
                  if (locality.latitude && locality.longitude) {
                    setUserHomeLocationCoords({ lat: locality.latitude, lon: locality.longitude });
                  }
                  console.log('✅ Updated home location (locality):', locality.name);
                } else {
                  setUserHomeLocationName(homeArea.display_name);
                }
              } else {
                setUserHomeLocationName(homeArea.display_name);
              }
            }

            // Reload all content for new home area (articles, chats, events)
            // An active visit takes precedence. Reads visitAreaRef — this listener's
            // dep array doesn't include visitArea.
            const feedBoundaryIds = resolveScopeBoundaryIds({
              fallbackBoundaryIds: refreshBoundaryIds,
              visitBoundaryIds: visitAreaRef.current?.boundaryIds
            });
            const [articlesData, chatsData, eventsData] = await Promise.all([
              fetchLocalityNews(userHomeAreaId, { boundaryIds: feedBoundaryIds }),
              chatService.getLocalityChats(feedBoundaryIds),
              fetchLocalityEvents(userHomeAreaId, { boundaryIds: feedBoundaryIds })
            ]);
            if (activeContextRef.current !== myContext) return; // stale — user navigated away
            setArticlesWithMeta(articlesData);
            setChats(chatsData);
            setEventsWithMeta(eventsData);
            console.log('✅ Content reloaded for new home location - Articles:', articlesData.length, 'Chats:', chatsData.length, 'Events:', eventsData.length);
          } else {
            console.warn('⚠️ No home area found for user');
            // Self-healer handles this case on auth resolve — no modal needed here
          }
        } else if (!currentUser?.id && currentContext === 'locality') {
          // Guest — re-derive the default guest locality rather than leaving the previous
          // authenticated session's boundary/name/content on screen (sign-out without a
          // reload is the case that exposed this; this event is dispatched with userId:null
          // from handleLogout, so this branch is what fires). Same resolver loadInitialData
          // uses on a cold guest load — one derivation, both triggers. Reloads articles, chats
          // AND events, matching a cold guest load exactly — handleGuestLocationChange (the
          // map-picker path) only reloads articles/events; that's a separate, pre-existing gap
          // left alone here (flagged for the backlog).
          // See docs/briefs/2026-08-13-guest-boundary-lost-on-signout.md.
          console.log('👤 Guest after locations refresh - re-deriving default guest locality...');
          stampContext('locality');
          const myContext = 'locality';

          const guestLocation = await resolveGuestDefaultLocation();
          // Unlike loadInitialData's cold-load guest branch (where these all start unset),
          // resolution failing HERE would otherwise leave the previous authenticated
          // session's name/boundary on screen — exactly the "name and boundary disagree"
          // state this brief exists to close. Reset to the same defaults a fresh guest
          // session starts with when nothing resolves, so the two can never disagree.
          setUserHomeLocationName(guestLocation.name || 'My Location');
          setUserHomeLocationCoords(
            guestLocation.lat && guestLocation.lon
              ? { lat: guestLocation.lat, lon: guestLocation.lon }
              : null
          );
          setDetectedBoundaryId(guestLocation.boundaryId || null);
          detectedBoundaryIdRef.current = guestLocation.boundaryId || null;

          const feedBoundaryIds = guestLocation.homeBoundaryIds;
          const [articlesData, chatsData, eventsData] = await Promise.all([
            fetchLocalityNews(null, { boundaryIds: feedBoundaryIds, guestCoords: guestLocation.guestCoords }),
            chatService.getLocalityChats(feedBoundaryIds),
            fetchLocalityEvents(null, { boundaryIds: feedBoundaryIds, guestCoords: guestLocation.guestCoords })
          ]);
          if (activeContextRef.current !== myContext) return; // stale — user navigated away
          setArticlesWithMeta(articlesData);
          setChats(chatsData);
          setEventsWithMeta(eventsData);
          // Every other guest content-load path marks this so a later context switch
          // preserves rather than clears (ournet:clear-content, App.js handleClearContent) —
          // this reload is no different.
          guestArticlesLoadedRef.current = true;
          console.log('✅ Guest content reloaded after sign-out - Articles:', articlesData.length, 'Chats:', chatsData.length, 'Events:', eventsData.length);
        }
      } catch (error) {
        console.error('❌ Error refreshing locations:', error);
      }
    };

    window.addEventListener('ournet:refresh-locations', handleLocationsRefresh);
    return () => window.removeEventListener('ournet:refresh-locations', handleLocationsRefresh);
  }, [currentUser, currentContext]); // eslint-disable-line react-hooks/exhaustive-deps -- stampContext reads only refs, setters and module state

  // Listen for auth required events (from AuthContext.requireAuth)
  useEffect(() => {
    const handleShowAuthRequired = (event) => {
      const { message } = event.detail || {};
      console.log('🔐 Auth required event received — opening login modal:', message);
      setAuthRequiredMessage(message || 'gate_body_generic');
      setLoginModalMode('login');
      setShowLoginModal(true);
    };

    window.addEventListener('ournet:showAuthRequired', handleShowAuthRequired);
    return () => window.removeEventListener('ournet:showAuthRequired', handleShowAuthRequired);
  }, []);

  // Listen for show login modal events (from AuthContext.goToLogin)
  useEffect(() => {
    const handleShowLogin = () => {
      console.log('🔑 Show login event received');
      setShowLoginModal(true);
    };

    window.addEventListener('ournet:show-login', handleShowLogin);
    return () => window.removeEventListener('ournet:show-login', handleShowLogin);
  }, []);

  // Listen for area changes (when user switches areas via LocalitySelector)
  useEffect(() => {
    const handleAreaChanged = async (event) => {
      const { areaId, areaCode, displayName } = event.detail;
      console.log('🔄 Area changed to:', areaCode, '(', displayName, ') - reloading content...');

      // Skip if we're on a demo route (demo handler manages group content)
      const currentPath = window.location.pathname;
      const isDemoRoute = currentPath.match(/^\/demo\/([^/]+)$/); // exactly /demo/<slug>

      if (isDemoRoute) {
        console.log('🎭 Demo route active - skipping area change content reload');
        return;
      }

      // Store selected area in App.js state so it persists across navigation
      setSelectedArea({ id: areaId, area_code: areaCode, display_name: displayName });

      // Area change is always a locality-context switch
      stampContext('locality');
      const myContext = 'locality';

      try {
        // Fetch the selected area including boundary_ids and primary_boundary_id
        const { data: switchedArea } = await supabaseFetch.query('user_areas', {
          select: 'id,boundary_ids,primary_boundary_id',
          eq: { id: areaId },
          single: true
        });

        const switchedBoundaryIds = switchedArea?.boundary_ids || [];

        // Enrich selectedArea with boundary_ids and primary_boundary_id so content creation forms can derive location_point
        if (switchedBoundaryIds.length > 0) {
          setSelectedArea(prev => ({
            ...prev,
            boundary_ids: switchedBoundaryIds,
            primary_boundary_id: switchedArea?.primary_boundary_id || null
          }));
        }

        if (switchedBoundaryIds.length > 0) {
          // New system: centroid from boundaries
          const { data: centroid } = await supabaseFetch.rpc('get_boundary_centroid', {
            p_boundary_ids: headlineBoundaryIds(switchedArea)
          });
          if (centroid?.[0]) {
            setUserHomeLocationName(centroid[0].name);
            setUserHomeLocationCoords({ lat: centroid[0].lat, lon: centroid[0].lon });
            console.log('✅ Updated location display to (boundary):', centroid[0].name);
          } else {
            setUserHomeLocationName(displayName);
          }
        } else {
          // Legacy system: first locality from user_area_localities
          const { data: areaLocalities } = await supabaseFetch.query('user_area_localities', {
            select: 'locality_id',
            eq: { area_id: areaId },
            order: 'display_order.asc',
            limit: 1
          });

          let homeLocality = null;
          if (areaLocalities?.[0]?.locality_id) {
            const { data: locality } = await supabaseFetch.query('localities', {
              select: 'name,latitude,longitude',
              eq: { id: areaLocalities[0].locality_id },
              single: true
            });
            homeLocality = locality;
          }

          if (homeLocality?.name) {
            setUserHomeLocationName(homeLocality.name);
            if (homeLocality.latitude && homeLocality.longitude) {
              setUserHomeLocationCoords({ lat: homeLocality.latitude, lon: homeLocality.longitude });
            }
            console.log('✅ Updated location display to (locality):', homeLocality.name);
          } else {
            setUserHomeLocationName(displayName);
          }
        }

        // Reload all content for the new area (articles, chats, events)
        // This resolver call generically
        // prefers an active visit when visitAreaRef.current is non-null (addendum-1 §C — a
        // background area-changed dispatch must not silently end a visit). But when THIS dispatch
        // is the dial's own "select an area slot" action, HeaderButtonsFeed's handleSelectArea
        // already called onClearVisit synchronously before firing this event (addendum-4 §2 — own-
        // area selection deliberately DOES end the visit), so visitAreaRef.current is already null
        // by the time this listener runs and the reload correctly uses switchedBoundaryIds instead.
        // Reads visitAreaRef, not visitArea state — this listener's dep array is [] (registered
        // once).
        const areaBoundaryIds = resolveScopeBoundaryIds({
          fallbackBoundaryIds: switchedBoundaryIds,
          visitBoundaryIds: visitAreaRef.current?.boundaryIds
        });
        const [articlesData, chatsData, eventsData] = await Promise.all([
          fetchLocalityNews(areaId, { boundaryIds: areaBoundaryIds }),
          chatService.getLocalityChats(areaBoundaryIds),
          fetchLocalityEvents(areaId, { boundaryIds: areaBoundaryIds })
        ]);
        if (activeContextRef.current !== myContext) return; // stale — user navigated away
        setArticlesWithMeta(articlesData);
        setChats(chatsData);
        setEventsWithMeta(eventsData);
        console.log('✅ Content reloaded for area', areaCode, '- Articles:', articlesData.length, 'Chats:', chatsData.length, 'Events:', eventsData.length);
      } catch (error) {
        console.error('❌ Error changing area:', error);
      }
    };

    window.addEventListener('ournet:area-changed', handleAreaChanged);
    return () => window.removeEventListener('ournet:area-changed', handleAreaChanged);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- stampContext reads only refs, setters and module state

  // Event listener for article search (full-text via search_articles RPC)
  useEffect(() => {
    const handleArticleSearch = async (event) => {
      if (isDemoReadActive()) return; // search is hidden in demo mode: search_articles only sees public
      const { searchTerm } = event.detail;
      console.log('🔍 App.js: Article search event received:', searchTerm);

      setArticleSearchTerm(searchTerm);

      if (searchTerm && searchTerm.trim()) {
        try {
          // Build scope: boundary IDs for locality, or community_id for groups
          let boundaryIds = null;
          let communityId = null;
          let articleType = null;

          if (currentContext === 'groups' && selectedGroup?.id) {
            // Group context: scope to the selected group
            communityId = selectedGroup.id;
          } else if (visitAreaRef.current?.boundaryIds?.length > 0) {
            // Active visit wins — search the visited locality, not home. Reads visitAreaRef, not
            // visitArea state: this listener's dep array doesn't include it.
            boundaryIds = visitAreaRef.current.boundaryIds;
          } else {
            // Locality context: use the user's home boundary IDs
            if (currentUser?.id) {
              // Fetch user's home boundary IDs
              const { data: homeArea } = await supabaseFetch.query('user_areas', {
                select: 'boundary_ids',
                eq: { user_id: currentUser.id, is_home: true },
                single: true
              });
              if (homeArea?.boundary_ids?.length > 0) {
                boundaryIds = homeArea.boundary_ids;
              }
            }
            // If in events mode, filter to events only
            if (currentMode === 'events') {
              articleType = 'event';
            }
          }

          const results = await articleService.searchArticles(searchTerm, {
            boundaryIds,
            communityId,
            articleType
          });
          console.log('✅ App.js: Search results:', results.length);
          setSearchedArticles(results);
        } catch (error) {
          console.error('❌ App.js: Error searching articles:', error);
          setSearchedArticles([]);
        }
      } else {
        // Clear search
        setSearchedArticles([]);
      }
    };

    window.addEventListener('ournet:search-articles', handleArticleSearch);
    return () => window.removeEventListener('ournet:search-articles', handleArticleSearch);
  }, [currentUser, currentContext, selectedGroup, currentMode]);

  // ============================================================================
  // THEME MANAGEMENT
  // ============================================================================
  
  const currentTheme = currentContext === 'locality' ? 'locality' : 'groups';

  // ============================================================================
  // LOADING STATE
  // ============================================================================
  
  if (loading) {
    return (
      <ThemeProvider theme={currentTheme}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '18px',
          background: '#EDF2F8'
        }}>
          Loading OurNet...
        </div>
      </ThemeProvider>
    );
  }

  // ============================================================================
  // MAIN RENDER
  // ============================================================================
  
  console.log('🎨 Rendering view:', currentView);

  return (
    <ThemeProvider theme={currentTheme}>
      <TranslationProvider userId={currentUser?.id || null} countryCode={countryInView}>
      <AuthProvider
        externalIsLoggedIn={isLoggedIn}
        externalCurrentUser={currentUser}
      >
      <div style={{ minWidth: '320px' }}>
        {/* One-shot sign-in strip (2026-09-16 stale-session-guard brief, items 3 and 4).
            Rendered here, inside <TranslationProvider>, so its strings are t() keys rather
            than new hardcoded English in App.js's pre-provider body. Above the view switch
            so it is visible whichever view the callback or the self-healer lands on. */}
        {signInNotice && (
          <SignInNotice
            status={signInNotice}
            onDismiss={() => setSignInNotice(null)}
          />
        )}
        {/* The way back for a member closing their account (#82, members-can-leave addendum 4).
            Keyed on the signed-in member's id, so every session start checks it; renders nothing
            unless the member is closing. Inside <TranslationProvider> like the strip above. */}
        <AccountReturnModal userId={currentUser?.id} onSignOut={handleLogout} />
        {/* New-account setup (#88, R4): a signed-in member with no home chooses their place, their
            area and an optional profile. Stands aside on an invitation page (R5: join first).
            Keyed on the member, so nothing half-chosen survives an account switch (Advisor A2). */}
        {currentUser?.id && setupState.userId === currentUser.id && setupState.needed === true
          && !INVITATION_VIEWS.has(currentView) && (
          <AccountSetup
            key={currentUser.id}
            userId={currentUser.id}
            onComplete={handleSetupComplete}
            onSignOut={handleLogout}
          />
        )}
        {/* Demo session expired (2026-09-25 brief + addendum 1). Rendered here, beside the sign-in
            strip and inside <TranslationProvider>, for the same reason: its strings are t() keys
            rather than new hardcoded English in App.js's pre-provider body. Renders NoticeStrip
            directly rather than adding a status to SignInNotice — see that file's header. */}
        {demoSessionExpired && (
          <NoticeStrip
            messageKey="demo_session_expired"
            namespace="auth"
            tone="error"
            action={{ labelKey: 'demo_session_expired_signin', onClick: handleDemoSessionSignIn }}
            onDismiss={() => setDemoSessionExpired(false)}
          />
        )}
        {/* MAIN FEED - Landing Page */}
        {currentView === 'feed' && (
          <MainFeed
            isDemoMode={isDemoMode}
            showDemoWatermark={isDemoMode || isKnownDemoId(selectedGroup?.id)}
            currentContext={currentContext}
            setCurrentContext={setCurrentContext}
            articles={articleSearchTerm ? searchedArticles : articles}
            articlesHasMore={articleSearchTerm ? false : articlesHasMore}
            feedPaging={currentContext === 'locality' && !articleSearchTerm ? {
              busy: loadingMore !== null,
              news: { hasMore: articlesHasMore, onLoadMore: loadMoreNews, loading: loadingMore === 'news' },
              future: { hasMore: eventsHasMore.future, onLoadMore: () => loadMoreEvents('future'), loading: loadingMore === 'future' },
              past: { hasMore: eventsHasMore.past, onLoadMore: () => loadMoreEvents('past'), loading: loadingMore === 'past' }
            } : null}
            chats={chats}
            events={events}
            articleSearchTerm={articleSearchTerm}
            onClearSearch={() => {
              setArticleSearchTerm('');
              setSearchedArticles([]);
            }}
            onArticleClick={handleArticleClick}
            onChatClick={handleChatClick}
            onNewChat={handleNewChat}
            onEventClick={handleEventClick}
            onLoadChatsForGroup={handleLoadChatsForGroup}
            onLoadArticlesForGroup={handleLoadArticlesForGroup}
            onLoadEventsForGroup={handleLoadEventsForGroup}
            onLoadLocalityArticles={handleLoadLocalityArticles}
            onGuestLocationChange={handleGuestLocationChange}
            visitArea={visitArea}
            onEnterVisit={handleEnterVisit}
            onClearVisit={handleClearVisit}
            currentUser={currentUser}
            selectedGroup={selectedGroup}
            setSelectedGroup={setSelectedGroup}
            setPreviousGroup={setPreviousGroup}
            onGroupGateBack={handleGroupGateBack}
            selectedArea={selectedArea}
            setSelectedArea={setSelectedArea}
            detectedBoundaryId={detectedBoundaryId}
            currentMode={currentMode}
            setCurrentMode={setCurrentMode}
            frontPageActive={frontPageActive}
            setFrontPageActive={setFrontPageActive}
            mobileScreen={mobileScreen}
            setMobileScreen={setMobileScreen}
            userHomeLocationName={userHomeLocationName}
            userHomeLocationCoords={userHomeLocationCoords}
            isLoggedIn={isLoggedIn}
            onShowLoginModal={handleShowLoginModal}
            sessionReady={!loading}
            groupsInitialMode={groupsInitialMode}
            setGroupsInitialMode={setGroupsInitialMode}
            feedScrollPosition={feedScrollPosition}
            shouldRestoreScroll={shouldRestoreScroll}
            onScrollRestored={() => setShouldRestoreScroll(false)}
            openChatThreadId={frontPageOpenChatId}
            onChatThreadToggle={handleFrontPageChatThreadToggle}
            onChatsRefresh={currentContext === 'groups' ? handleGroupChatsRefresh : handleLocalityChatsRefresh}
          />
        )}

      {/* ARTICLE DETAIL */}
      {/*
        THEME: Determined by community_id presence.
        - community_id present → group article → mauve theme
        - community_id null    → locality article → blue theme
      */}
      {currentView === 'article' && (
        <ThemeProvider theme={selectedArticle?.community_id ? 'groups' : 'locality'}>
          <ArticleDetail
            article={selectedArticle}
            onBack={handleBackToFeed}
            onViewGroup={handleViewGroup}
            context={currentContext}
            isLoggedIn={isLoggedIn}
            onShowLoginModal={handleShowLoginModal}
            userId={currentUser?.id}
            articles={articles}
            onArticleClick={handleArticleClick}
          />
          {(isDemoMode || isKnownDemoId(selectedArticle?.id)) && <DemoWatermark />}
        </ThemeProvider>
      )}

      {/* CHAT DETAIL */}
      {currentView === 'chat' && (
        <>
          <ChatDetail
            chat={selectedChat}
            responses={selectedChatResponses}
            onBack={handleBackToFeed}
            currentContext={currentContext}
            selectedGroup={selectedGroup}
            homeLocalityName={userHomeLocationName}
            onPostResponse={handlePostResponse}
            isLoggedIn={isLoggedIn}
            onShowLoginModal={handleShowLoginModal}
            userId={currentUser?.id}
          />
          {(isDemoMode || isKnownDemoId(selectedChat?.id)) && <DemoWatermark />}
        </>
      )}

      {/* EVENT DETAIL */}
      {currentView === 'event' && (
        <>
          <EventsDetail
            event={selectedEvent}
            onBack={handleBackToFeed}
            onViewGroup={handleViewGroup}
            context={currentContext}
            isLoggedIn={isLoggedIn}
            onShowLoginModal={handleShowLoginModal}
            userId={currentUser?.id}
          />
          {(isDemoMode || isKnownDemoId(selectedEvent?.id)) && <DemoWatermark />}
        </>
      )}

      {/* MY PROFILE */}
      {currentView === 'profile' && (
        <MyProfile
          userId={selectedProfileUserId}
          onBack={handleBackFromMy}
          currentContext={currentContext}
          onLogout={handleLogout}
        />
      )}

      {/* MY ARTICLES */}
      {currentView === 'articles' && (
        <MyArticles
          userId={selectedProfileUserId}
          onBack={handleBackFromMy}
          currentContext={currentContext}
          onArticleClick={handleArticleClick}
          onEventClick={handleEventClick}
          isVisiting={!!visitArea}
        />
      )}

      {/* MY TAGS */}
      {currentView === 'tags' && (
        <MyTags
          userId={selectedProfileUserId}
          onBack={handleBackFromMy}
          currentContext={currentContext}
        />
      )}

      {/* MY LOCATIONS */}
      {currentView === 'locations' && (
        <MyLocations
          userId={selectedProfileUserId}
          onBack={() => { setAutoHomeHighlight(false); setMyLocationsSetHomeSeed(null); handleBackFromMy(); }}
          currentContext={currentContext}
          autoHomeHighlight={autoHomeHighlight}
          onAutoHomeHighlightDismiss={() => setAutoHomeHighlight(false)}
          setHomeSeedBoundaryId={myLocationsSetHomeSeed?.boundaryId ?? null}
        />
      )}

      {/* STEWARD WORKSPACE */}
      {currentView === 'steward' && (
        <StewardWorkspace
          currentUser={currentUser}
          onBack={handleBackFromMy}
        />
      )}

      {/* EVENTS IMPORTER (Phase 4) */}
      {currentView === 'events-importer' && (
        <EventsImporter
          currentUser={currentUser}
          onBack={handleBackFromMy}
          currentContext={currentContext}
        />
      )}

      {/* PENDING EVENTS (Phase 4 §6) */}
      {currentView === 'events-pending' && (
        <PendingEvents
          currentUser={currentUser}
          onBack={handleBackFromMy}
          currentContext={currentContext}
        />
      )}

      {/* MY IMAGES */}
      {currentView === 'images' && (
        <MyImages
          userId={selectedProfileUserId}
          onBack={handleBackFromMy}
        />
      )}

      {/* NEW/EDIT ARTICLE */}
      {currentView === 'new-article' && (
        <AddEditArticle
          userId={selectedProfileUserId}
          onBack={handleBackFromMy}
          currentContext={currentContext}
          mode={articleMode}
          article={articleToEdit}
          preselectedGroupId={preselectedGroupId}
          localityPublishCoords={userHomeLocationCoords}
          selectedArea={selectedArea}
        />
      )}

      {/* WALKING COMPOSER */}
      {currentView === 'walking-composer' && (
        <WalkingComposer
          userId={selectedProfileUserId}
          onBack={handleBackFromMy}
          selectedArea={selectedArea}
          localityPublishCoords={userHomeLocationCoords}
          groupId={preselectedGroupId}
        />
      )}

      {/* NEW/EDIT CHAT */}
      {currentView === 'new-chat' && (
        <AddEditChat
          userId={selectedProfileUserId}
          onBack={handleBackFromMy}
          currentContext={currentContext}
          mode={chatMode}
          chat={chatToEdit}
          preselectedGroupId={preselectedChatGroupId}
          selectedArea={selectedArea}
          visitBoundaryId={visitArea?.boundaryIds?.[0] || null}
        />
      )}

      {/* NEW/EDIT EVENT */}
      {currentView === 'new-event' && (
        <AddEditEvent
          userId={selectedProfileUserId}
          onBack={handleBackFromMy}
          currentContext={currentContext}
          mode={eventMode}
          event={eventToEdit}
          preselectedGroupId={preselectedEventGroupId}
          selectedArea={selectedArea}
        />
      )}

      {/* ADD/EDIT GROUP */}
      {currentView === 'new-group' && (
        <AddEditGroup
          userId={selectedProfileUserId}
          onBack={handleBackFromMy}
          currentContext={currentContext}
          mode={groupMode}
          group={groupToEdit}
          parentGroup={parentGroupForNew}
          prefill={groupPrefill}
        />
      )}

      {/* ADMIN GROUP */}
      {currentView === 'admin-group' && (
        <AdminGroup
          userId={selectedProfileUserId}
          onBack={handleBackFromMy}
          currentContext={currentContext}
          group={groupToEdit}
          postCreate={postCreateGroup}
        />
      )}

      {/* GROUP LANDING PAGE EDITOR */}
      {currentView === 'group-landing' && (
        <AddEditLandingPageGroup
          userId={selectedProfileUserId}
          // Setup-end handoff: skip/leave lands on admin-group (the existing "done" target),
          // not wherever the create form happened to be opened from.
          onBack={postCreateLandingPage
            ? () => handleShowAdminGroup(addEditLandingPageGroup, true)
            : handleBackFromMy}
          group={addEditLandingPageGroup}
          postCreate={postCreateLandingPage}
        />
      )}

      {/* GROUP LANDING PAGE VIEW (Read-Only) */}
      {currentView === 'group-landing-view' && (
        <LandingPageGroup
          group={landingPageGroup}
          onBack={landingFromGate ? handleGroupGateBack : handleBackFromMy}
          currentUser={currentUser}
          onContinueToGroup={async (group) => {
            if (!group?.id) return;
            setCurrentContext('groups');
            setSelectedGroup(group);
            setCurrentMode('home'); // Tier 2: continue into the group on its Home/Info tab
            setLandingFromGate(false);
            await handleLoadArticlesForGroup(group.id);
            await handleLoadChatsForGroup(group.id);
            await handleLoadEventsForGroup(group.id);
            setCurrentView('feed');
            setPreviousContext(null);
          }}
        />
      )}

      {/* ACCEPT INVITATION (personal link) — leaves by ournet:navigate ('group' / 'feed') */}
      {currentView === 'accept-invitation' && (
        <AcceptInvitation
          inviteToken={inviteToken}
          currentUser={currentUser}
          onShowLogin={showInvitationLogin}
        />
      )}

      {/* SHARED JOIN LINK (Invitations Phase 3, #54) */}
      {currentView === 'join-group-link' && (
        <JoinGroupLink
          joinToken={joinToken}
          currentUser={currentUser}
          onShowLogin={showInvitationLogin}
        />
      )}

      {/* INVITATION EMAIL: OPT-OUT / REPORT (no account; nothing runs until a button press) */}
      {(currentView === 'invitation-optout' || currentView === 'invitation-report') && (
        <InvitationEmailAction
          key={`${currentView}:${emailActionToken}`}
          mode={currentView === 'invitation-report' ? 'report' : 'optout'}
          token={emailActionToken}
        />
      )}

      {/* ADMIN ANALYTICS */}
      {currentView === 'admin-analytics' && (
        <AdminAnalytics
          onBack={handleBackFromMy}
          currentUser={currentUser}
        />
      )}

      {/* ADMIN PANEL */}
      {currentView === 'admin' && (
        <AdminPanel
          onBack={handleBackFromMy}
          currentUser={currentUser}
        />
      )}

      {/* AUTHORITY VERIFICATION */}
      {currentView === 'authority-verify' && authorityVerifyToken && (
        <AuthorityVerifyPage
          token={authorityVerifyToken}
          onViewGroup={handleViewGroup}
        />
      )}

      {/* PUBLIC DIRECTORY-SUGGESTION PAGE (no account) */}
      {currentView === 'suggest' && (
        <SuggestPage />
      )}

      {/* LOGIN/REGISTER MODAL */}
      <LoginRegisterModal
        visible={showLoginModal}
        initialMode={loginModalMode}
        gateMessage={authRequiredMessage}
        inviteContext={
          currentView === 'accept-invitation' && inviteToken ? { type: 'invite', token: inviteToken }
            : currentView === 'join-group-link' && joinToken ? { type: 'join', token: joinToken }
              : null
        }
        onClose={handleCloseLoginModal}
        onLoginSuccess={handleLoginSuccess}
        isLoggedIn={isLoggedIn}
        setIsLoggedIn={setIsLoggedIn}
      />

      {/* RESET PASSWORD MODAL */}
      <ResetPasswordModal
        visible={showResetPasswordModal}
        onClose={() => setShowResetPasswordModal(false)}
      />

      {/* APP MODAL - Generic info/error messages replacing window.alert */}
      <ConfirmationModal
        visible={appModal.visible}
        message={appModal.message}
        onOk={() => setAppModal({ visible: false, message: '' })}
        okLabel="OK"
      />

      {/* LocationSetupModal removed — replaced by homeAreaSelfHealer */}

      {/* The required map picker (the old registration gate when geolocation failed) is retired:
          a member with no home sets up their account in AccountSetup (#88). */}

      {/* SMART LINK PREVIEW - Shown when non-member visits a smart link */}
      <SmartLinkPreview
        visible={showSmartLinkPreview}
        targetType={smartLinkData?.target_type || 'community'}
        targetData={smartLinkTarget}
        linkData={smartLinkData}
        isLoggedIn={isLoggedIn}
        onJoin={handleSmartLinkJoin}
        onLogin={() => {
          setShowSmartLinkPreview(false);
          setShowLoginModal(true);
        }}
        onClose={() => {
          setShowSmartLinkPreview(false);
          setSmartLinkData(null);
          setSmartLinkTarget(null);
          // Navigate to feed
          window.history.pushState(null, '', '/');
          setCurrentView('feed');
        }}
        isLoading={smartLinkLoading}
      />

      </div>
      </AuthProvider>
      </TranslationProvider>
    </ThemeProvider>
  );
}

export default App;